import type { McpTool } from "@p0security/shared/types/agentic/mcp";

// Listed by PagerDuty's US MCP server on 2026-10-05. Its tools/list needs a
// token, which P0 does not hold when it syncs a gateway.
export const pagerdutyMcpTools: McpTool[] = [
  {
    name: "browse_activity",
    description:
      "Read PagerDuty account activity (log entries).\n\nActions: list_log_entries (all log entries, filter by time range; defaults to last 7 days),\nget_log_entry (one log entry by ID).\n\nActions:\n- list_log_entries: required=[], optional=[since, until, limit, offset, is_overview, include]\n- get_log_entry: required=[log_entry_id], optional=[]",
    inputSchema: {
      additionalProperties: false,
      properties: {
        request: {
          oneOf: [
            {
              properties: {
                action: {
                  const: "list_log_entries",
                  default: "list_log_entries",
                  type: "string",
                },
                since: {
                  anyOf: [
                    {
                      format: "date-time",
                      type: "string",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description: "ISO 8601 start time (default: 7 days ago)",
                },
                until: {
                  anyOf: [
                    {
                      format: "date-time",
                      type: "string",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description: "ISO 8601 end time (default: now)",
                },
                limit: {
                  default: 100,
                  description: "Maximum number of results to return",
                  maximum: 100,
                  minimum: 1,
                  type: "integer",
                },
                offset: {
                  anyOf: [
                    {
                      minimum: 0,
                      type: "integer",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Offset for pagination; use after a truncated=True response",
                },
                is_overview: {
                  anyOf: [
                    {
                      type: "boolean",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "If true, returns only the most important changes to the incident",
                },
                include: {
                  anyOf: [
                    {
                      items: {
                        type: "string",
                      },
                      type: "array",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Array of additional models to include. Options: 'incidents', 'services', 'channels', 'teams'",
                },
              },
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "get_log_entry",
                  default: "get_log_entry",
                  type: "string",
                },
                log_entry_id: {
                  type: "string",
                },
              },
              required: ["log_entry_id"],
              type: "object",
            },
          ],
        },
      },
      required: ["request"],
      type: "object",
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
    },
  },
  {
    name: "browse_alert_grouping",
    description:
      "Read PagerDuty alert grouping settings.\n\nActions: list (filter by service IDs, paginate by cursor),\nget (one setting by ID).\n\nActions:\n- list: required=[], optional=[service_ids, limit, after, before, total]\n- get: required=[setting_id], optional=[]",
    inputSchema: {
      additionalProperties: false,
      properties: {
        request: {
          oneOf: [
            {
              properties: {
                action: {
                  const: "list",
                  default: "list",
                  type: "string",
                },
                service_ids: {
                  anyOf: [
                    {
                      items: {
                        type: "string",
                      },
                      type: "array",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                },
                limit: {
                  default: 100,
                  description: "Maximum number of results to return",
                  maximum: 100,
                  minimum: 1,
                  type: "integer",
                },
                after: {
                  anyOf: [
                    {
                      type: "string",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                },
                before: {
                  anyOf: [
                    {
                      type: "string",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                },
                total: {
                  default: false,
                  type: "boolean",
                },
              },
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "get",
                  default: "get",
                  type: "string",
                },
                setting_id: {
                  type: "string",
                },
              },
              required: ["setting_id"],
              type: "object",
            },
          ],
        },
      },
      required: ["request"],
      type: "object",
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
    },
  },
  {
    name: "browse_change_events",
    description:
      "Read PagerDuty change event data.\n\nActions: list (all change events, filter by time/team/integration),\nget (one change event by ID),\nlist_service (change events for a specific service).\n\nActions:\n- list: required=[], optional=[since, until, limit, offset, total, team_ids, integration_ids]\n- get: required=[change_event_id], optional=[]\n- list_service: required=[service_id], optional=[since, until, limit, offset, total, team_ids, integration_ids]",
    inputSchema: {
      additionalProperties: false,
      properties: {
        request: {
          oneOf: [
            {
              properties: {
                action: {
                  const: "list",
                  default: "list",
                  type: "string",
                },
                since: {
                  anyOf: [
                    {
                      format: "date-time",
                      type: "string",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description: "ISO 8601 start time",
                },
                until: {
                  anyOf: [
                    {
                      format: "date-time",
                      type: "string",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description: "ISO 8601 end time",
                },
                limit: {
                  default: 100,
                  description: "Maximum number of results to return",
                  maximum: 100,
                  minimum: 1,
                  type: "integer",
                },
                offset: {
                  anyOf: [
                    {
                      minimum: 0,
                      type: "integer",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Offset for pagination; use after a truncated=True response",
                },
                total: {
                  anyOf: [
                    {
                      type: "boolean",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                },
                team_ids: {
                  anyOf: [
                    {
                      items: {
                        type: "string",
                      },
                      type: "array",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                },
                integration_ids: {
                  anyOf: [
                    {
                      items: {
                        type: "string",
                      },
                      type: "array",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                },
              },
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "get",
                  default: "get",
                  type: "string",
                },
                change_event_id: {
                  type: "string",
                },
              },
              required: ["change_event_id"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "list_service",
                  default: "list_service",
                  type: "string",
                },
                service_id: {
                  type: "string",
                },
                since: {
                  anyOf: [
                    {
                      format: "date-time",
                      type: "string",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description: "ISO 8601 start time",
                },
                until: {
                  anyOf: [
                    {
                      format: "date-time",
                      type: "string",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description: "ISO 8601 end time",
                },
                limit: {
                  default: 100,
                  description: "Maximum number of results to return",
                  maximum: 100,
                  minimum: 1,
                  type: "integer",
                },
                offset: {
                  anyOf: [
                    {
                      minimum: 0,
                      type: "integer",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Offset for pagination; use after a truncated=True response",
                },
                total: {
                  anyOf: [
                    {
                      type: "boolean",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                },
                team_ids: {
                  anyOf: [
                    {
                      items: {
                        type: "string",
                      },
                      type: "array",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                },
                integration_ids: {
                  anyOf: [
                    {
                      items: {
                        type: "string",
                      },
                      type: "array",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                },
              },
              required: ["service_id"],
              type: "object",
            },
          ],
        },
      },
      required: ["request"],
      type: "object",
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
    },
  },
  {
    name: "browse_escalation_policies",
    description:
      "Read PagerDuty escalation policy data.\n\nActions: list (filter by name/user/team), get (one policy by ID).\n\nActions:\n- list: required=[], optional=[query, user_ids, team_ids, include, limit, offset]\n- get: required=[policy_id], optional=[]",
    inputSchema: {
      additionalProperties: false,
      properties: {
        request: {
          oneOf: [
            {
              properties: {
                action: {
                  const: "list",
                  default: "list",
                  type: "string",
                },
                query: {
                  anyOf: [
                    {
                      type: "string",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                },
                user_ids: {
                  anyOf: [
                    {
                      items: {
                        type: "string",
                      },
                      type: "array",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                },
                team_ids: {
                  anyOf: [
                    {
                      items: {
                        type: "string",
                      },
                      type: "array",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                },
                include: {
                  anyOf: [
                    {
                      items: {
                        enum: ["services", "teams"],
                        type: "string",
                      },
                      type: "array",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                },
                limit: {
                  default: 100,
                  description: "Maximum number of results to return",
                  maximum: 100,
                  minimum: 1,
                  type: "integer",
                },
                offset: {
                  anyOf: [
                    {
                      minimum: 0,
                      type: "integer",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Offset for pagination; use after a truncated=True response",
                },
              },
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "get",
                  default: "get",
                  type: "string",
                },
                policy_id: {
                  type: "string",
                },
              },
              required: ["policy_id"],
              type: "object",
            },
          ],
        },
      },
      required: ["request"],
      type: "object",
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
    },
  },
  {
    name: "browse_event_orchestrations",
    description:
      "Read PagerDuty event orchestration data.\n\nActions: list (all orchestrations), get (one orchestration by ID),\nget_router (router configuration for an orchestration),\nget_service (service orchestration for a service ID),\nget_global (global orchestration for an orchestration ID).\n\nActions:\n- list: required=[], optional=[limit, offset, sort_by]\n- get: required=[orchestration_id], optional=[]\n- get_router: required=[orchestration_id], optional=[]\n- get_service: required=[service_id], optional=[]\n- get_global: required=[orchestration_id], optional=[]",
    inputSchema: {
      additionalProperties: false,
      properties: {
        request: {
          oneOf: [
            {
              properties: {
                action: {
                  const: "list",
                  default: "list",
                  type: "string",
                },
                limit: {
                  default: 100,
                  description: "Maximum number of results to return",
                  maximum: 100,
                  minimum: 1,
                  type: "integer",
                },
                offset: {
                  anyOf: [
                    {
                      minimum: 0,
                      type: "integer",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Offset for pagination; use after a truncated=True response",
                },
                sort_by: {
                  anyOf: [
                    {
                      enum: [
                        "name:asc",
                        "name:desc",
                        "routes:asc",
                        "routes:desc",
                        "created_at:asc",
                        "created_at:desc",
                      ],
                      type: "string",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: "name:asc",
                },
              },
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "get",
                  default: "get",
                  type: "string",
                },
                orchestration_id: {
                  type: "string",
                },
              },
              required: ["orchestration_id"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "get_router",
                  default: "get_router",
                  type: "string",
                },
                orchestration_id: {
                  type: "string",
                },
              },
              required: ["orchestration_id"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "get_service",
                  default: "get_service",
                  type: "string",
                },
                service_id: {
                  type: "string",
                },
              },
              required: ["service_id"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "get_global",
                  default: "get_global",
                  type: "string",
                },
                orchestration_id: {
                  type: "string",
                },
              },
              required: ["orchestration_id"],
              type: "object",
            },
          ],
        },
      },
      required: ["request"],
      type: "object",
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
    },
  },
  {
    name: "browse_incidents",
    description:
      "Read PagerDuty incident data.\n\nActions: list (filter by scope/status/urgency/priority/time/service), get (one incident by ID),\nlist_alerts (alerts on an incident), get_alert (one alert by ID),\nlist_notes (notes on an incident), context (related|past|outlier intelligence),\nlist_change_events (change events linked to an incident),\nlist_workflows (available incident workflows), get_workflow (one workflow by ID).\n\nActions:\n- list: required=[], optional=[request_scope, statuses, since, until, urgencies, priorities, service_ids, team_ids, limit, offset]\n- get: required=[incident_id], optional=[include]\n- list_alerts: required=[incident_id], optional=[limit, offset]\n- get_alert: required=[incident_id, alert_id], optional=[]\n- list_notes: required=[incident_id], optional=[limit, offset]\n- context: required=[incident_id, context_type], optional=[limit, additional_details]\n- list_change_events: required=[incident_id], optional=[limit, offset]\n- list_workflows: required=[], optional=[limit, offset, query, include]\n- get_workflow: required=[workflow_id], optional=[]",
    inputSchema: {
      additionalProperties: false,
      properties: {
        request: {
          oneOf: [
            {
              properties: {
                action: {
                  const: "list",
                  default: "list",
                  type: "string",
                },
                request_scope: {
                  anyOf: [
                    {
                      enum: ["all", "teams", "assigned"],
                      type: "string",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                },
                statuses: {
                  anyOf: [
                    {
                      items: {
                        enum: ["triggered", "acknowledged", "resolved"],
                        type: "string",
                      },
                      type: "array",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                },
                since: {
                  anyOf: [
                    {
                      format: "date-time",
                      type: "string",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description: "ISO 8601 start time",
                },
                until: {
                  anyOf: [
                    {
                      format: "date-time",
                      type: "string",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description: "ISO 8601 end time",
                },
                urgencies: {
                  anyOf: [
                    {
                      items: {
                        enum: ["high", "low"],
                        type: "string",
                      },
                      type: "array",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                },
                priorities: {
                  anyOf: [
                    {
                      items: {
                        type: "string",
                      },
                      type: "array",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Filter by priority ID (not priority name/level)",
                },
                service_ids: {
                  anyOf: [
                    {
                      items: {
                        type: "string",
                      },
                      type: "array",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description: "Filter by service ID",
                },
                team_ids: {
                  anyOf: [
                    {
                      items: {
                        type: "string",
                      },
                      type: "array",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Filter by team ID; overrides the caller's own teams when request_scope='teams'",
                },
                limit: {
                  default: 100,
                  description: "Maximum number of results to return",
                  maximum: 100,
                  minimum: 1,
                  type: "integer",
                },
                offset: {
                  anyOf: [
                    {
                      minimum: 0,
                      type: "integer",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Offset for pagination; use after a truncated=True response",
                },
              },
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "get",
                  default: "get",
                  type: "string",
                },
                incident_id: {
                  type: "string",
                },
                include: {
                  anyOf: [
                    {
                      items: {
                        type: "string",
                      },
                      type: "array",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                },
              },
              required: ["incident_id"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "list_alerts",
                  default: "list_alerts",
                  type: "string",
                },
                incident_id: {
                  type: "string",
                },
                limit: {
                  default: 100,
                  description: "Maximum number of results to return",
                  maximum: 100,
                  minimum: 1,
                  type: "integer",
                },
                offset: {
                  anyOf: [
                    {
                      minimum: 0,
                      type: "integer",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Offset for pagination; use after a truncated=True response",
                },
              },
              required: ["incident_id"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "get_alert",
                  default: "get_alert",
                  type: "string",
                },
                incident_id: {
                  type: "string",
                },
                alert_id: {
                  type: "string",
                },
              },
              required: ["incident_id", "alert_id"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "list_notes",
                  default: "list_notes",
                  type: "string",
                },
                incident_id: {
                  type: "string",
                },
                limit: {
                  default: 20,
                  description: "Maximum number of results to return",
                  maximum: 100,
                  minimum: 1,
                  type: "integer",
                },
                offset: {
                  anyOf: [
                    {
                      minimum: 0,
                      type: "integer",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Offset for pagination; use after a truncated=True response",
                },
              },
              required: ["incident_id"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "context",
                  default: "context",
                  type: "string",
                },
                incident_id: {
                  type: "string",
                },
                context_type: {
                  description:
                    "Which incident-intelligence lens to use: 'related' returns incidents PagerDuty has identified as related to this one; 'past' returns similar historical incidents ranked by similarity score; 'outlier' reports whether this incident deviates from the normal alert/incident pattern for its service.",
                  enum: ["related", "past", "outlier"],
                  type: "string",
                },
                limit: {
                  anyOf: [
                    {
                      maximum: 100,
                      minimum: 1,
                      type: "integer",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Past incidents only: max results to return (default varies by lens)",
                },
                additional_details: {
                  anyOf: [
                    {
                      items: {
                        type: "string",
                      },
                      type: "array",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Related incidents only: additional attributes to include",
                },
              },
              required: ["incident_id", "context_type"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "list_change_events",
                  default: "list_change_events",
                  type: "string",
                },
                incident_id: {
                  type: "string",
                },
                limit: {
                  default: 100,
                  description: "Maximum number of results to return",
                  maximum: 100,
                  minimum: 1,
                  type: "integer",
                },
                offset: {
                  anyOf: [
                    {
                      minimum: 0,
                      type: "integer",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Offset for pagination; use after a truncated=True response",
                },
              },
              required: ["incident_id"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "list_workflows",
                  default: "list_workflows",
                  type: "string",
                },
                limit: {
                  default: 100,
                  description: "Maximum number of results to return",
                  maximum: 100,
                  minimum: 1,
                  type: "integer",
                },
                offset: {
                  anyOf: [
                    {
                      minimum: 0,
                      type: "integer",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Offset for pagination; use after a truncated=True response",
                },
                query: {
                  anyOf: [
                    {
                      type: "string",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                },
                include: {
                  anyOf: [
                    {
                      items: {
                        enum: ["steps", "team"],
                        type: "string",
                      },
                      type: "array",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                },
              },
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "get_workflow",
                  default: "get_workflow",
                  type: "string",
                },
                workflow_id: {
                  type: "string",
                },
              },
              required: ["workflow_id"],
              type: "object",
            },
          ],
        },
      },
      required: ["request"],
      type: "object",
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
    },
  },
  {
    name: "browse_schedules",
    description:
      "Read PagerDuty schedules and on-call data.\n\nA schedule is either 'legacy' (v2, layer-based) or 'shift_based' (v3, built from rotations,\ncustom shifts and overrides). list, get, list_users and list_oncalls (who is on call) work\nfor both. The rotation, rotation_event, custom_shift and override actions are shift_based\nonly.\n\nActions:\n- list: required=[], optional=[query, team_ids, user_ids, include, kind, limit, offset]\n- get: required=[schedule_id], optional=[kind]\n- list_users: required=[schedule_id], optional=[]\n- list_oncalls: required=[], optional=[time_zone, user_ids, escalation_policy_ids, schedule_ids, service_ids, since, until, earliest, limit, offset]\n- list_rotations: required=[schedule_id], optional=[limit, offset]\n- get_rotation: required=[schedule_id, rotation_id], optional=[]\n- list_rotation_events: required=[schedule_id, rotation_id], optional=[limit, offset]\n- get_rotation_event: required=[schedule_id, rotation_id, event_id], optional=[]\n- list_custom_shifts: required=[schedule_id, since, until], optional=[limit, offset]\n- get_custom_shift: required=[schedule_id, custom_shift_id], optional=[]\n- list_overrides: required=[schedule_id, since, until], optional=[limit, offset]\n- get_override: required=[schedule_id, override_id], optional=[]",
    inputSchema: {
      additionalProperties: false,
      properties: {
        request: {
          oneOf: [
            {
              properties: {
                action: {
                  const: "list",
                  default: "list",
                  type: "string",
                },
                query: {
                  anyOf: [
                    {
                      type: "string",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description: "Filter by schedule name",
                },
                team_ids: {
                  anyOf: [
                    {
                      items: {
                        type: "string",
                      },
                      type: "array",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description: "Filter by team IDs",
                },
                user_ids: {
                  anyOf: [
                    {
                      items: {
                        type: "string",
                      },
                      type: "array",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Filter by member user IDs. Setting it lists legacy (v2) schedules only; shift-based (v3) schedules are not searched.",
                },
                include: {
                  anyOf: [
                    {
                      items: {
                        enum: [
                          "schedule_layers",
                          "overrides_subschedule",
                          "final_schedule",
                        ],
                        type: "string",
                      },
                      type: "array",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Extra detail to embed in each legacy (v2) schedule. Setting it lists legacy schedules only; shift-based (v3) schedules are not searched. 'schedule_layers': each layer's rotation configuration (users, turn length, start). 'overrides_subschedule': rendered entries produced by overrides alone. 'final_schedule': rendered on-call entries after layers and overrides are combined",
                },
                kind: {
                  anyOf: [
                    {
                      enum: ["legacy", "shift_based"],
                      type: "string",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Restrict results to one scheduling system: 'legacy' (v2) or 'shift_based' (v3). Omit to list both kinds together in one name-ordered list, which offset pages through. When omitted, items have no time_zone; use get for it.",
                },
                limit: {
                  default: 100,
                  description: "Maximum number of results to return",
                  maximum: 100,
                  minimum: 1,
                  type: "integer",
                },
                offset: {
                  anyOf: [
                    {
                      minimum: 0,
                      type: "integer",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Offset for pagination; use after a truncated=True response",
                },
              },
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "get",
                  default: "get",
                  type: "string",
                },
                schedule_id: {
                  type: "string",
                },
                kind: {
                  anyOf: [
                    {
                      enum: ["legacy", "shift_based"],
                      type: "string",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Omit unless certain; the tool detects the kind. 'shift_based' saves a lookup but fails for a legacy ID.",
                },
              },
              required: ["schedule_id"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "list_users",
                  default: "list_users",
                  type: "string",
                },
                schedule_id: {
                  type: "string",
                },
              },
              required: ["schedule_id"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "list_oncalls",
                  default: "list_oncalls",
                  type: "string",
                },
                time_zone: {
                  anyOf: [
                    {
                      type: "string",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "IANA time zone to render results in, e.g. 'America/New_York'; defaults to the account time zone",
                },
                user_ids: {
                  anyOf: [
                    {
                      items: {
                        type: "string",
                      },
                      type: "array",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description: "Filter by user IDs",
                },
                escalation_policy_ids: {
                  anyOf: [
                    {
                      items: {
                        type: "string",
                      },
                      type: "array",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description: "Filter by escalation policy IDs",
                },
                schedule_ids: {
                  anyOf: [
                    {
                      items: {
                        type: "string",
                      },
                      type: "array",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description: "Filter by schedule IDs",
                },
                service_ids: {
                  anyOf: [
                    {
                      items: {
                        type: "string",
                      },
                      type: "array",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Filter by the escalation policies of these service IDs",
                },
                since: {
                  anyOf: [
                    {
                      format: "date-time",
                      type: "string",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Start of the range; on-call periods overlapping it are included. Defaults to now. ISO 8601, e.g. '2026-09-04T13:00:00Z'",
                },
                until: {
                  anyOf: [
                    {
                      format: "date-time",
                      type: "string",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "End of the range, at most 90 days ahead. Defaults to now. ISO 8601, e.g. '2026-09-04T13:00:00Z'",
                },
                earliest: {
                  anyOf: [
                    {
                      type: "boolean",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: true,
                  description:
                    "Return only the earliest on-call per escalation policy, level and user; set false to list every on-call period in the range",
                },
                limit: {
                  default: 100,
                  description: "Maximum number of results to return",
                  maximum: 100,
                  minimum: 1,
                  type: "integer",
                },
                offset: {
                  anyOf: [
                    {
                      minimum: 0,
                      type: "integer",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Offset for pagination; use after a truncated=True response",
                },
              },
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "list_rotations",
                  default: "list_rotations",
                  type: "string",
                },
                schedule_id: {
                  description:
                    "The ID of the schedule whose rotations are listed",
                  type: "string",
                },
                limit: {
                  default: 100,
                  description: "Maximum number of results to return",
                  maximum: 100,
                  minimum: 1,
                  type: "integer",
                },
                offset: {
                  anyOf: [
                    {
                      minimum: 0,
                      type: "integer",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Offset for pagination; use after a truncated=True response",
                },
              },
              required: ["schedule_id"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "get_rotation",
                  default: "get_rotation",
                  type: "string",
                },
                schedule_id: {
                  description: "The ID of the schedule the rotation belongs to",
                  type: "string",
                },
                rotation_id: {
                  description: "The ID of the rotation to fetch",
                  type: "string",
                },
              },
              required: ["schedule_id", "rotation_id"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "list_rotation_events",
                  default: "list_rotation_events",
                  type: "string",
                },
                schedule_id: {
                  description: "The ID of the schedule the rotation belongs to",
                  type: "string",
                },
                rotation_id: {
                  description: "The ID of the rotation whose events are listed",
                  type: "string",
                },
                limit: {
                  default: 100,
                  description: "Maximum number of results to return",
                  maximum: 100,
                  minimum: 1,
                  type: "integer",
                },
                offset: {
                  anyOf: [
                    {
                      minimum: 0,
                      type: "integer",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Offset for pagination; use after a truncated=True response",
                },
              },
              required: ["schedule_id", "rotation_id"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "get_rotation_event",
                  default: "get_rotation_event",
                  type: "string",
                },
                schedule_id: {
                  description: "The ID of the schedule the rotation belongs to",
                  type: "string",
                },
                rotation_id: {
                  description: "The ID of the rotation the event belongs to",
                  type: "string",
                },
                event_id: {
                  description: "The ID of the rotation event to fetch",
                  type: "string",
                },
              },
              required: ["schedule_id", "rotation_id", "event_id"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "list_custom_shifts",
                  default: "list_custom_shifts",
                  type: "string",
                },
                schedule_id: {
                  description:
                    "The ID of the schedule whose custom shifts are listed",
                  type: "string",
                },
                since: {
                  description:
                    "Start of the time range to list custom shifts within. ISO 8601 with UTC offset, e.g. '2026-09-04T13:00:00Z'",
                  format: "date-time",
                  type: "string",
                },
                until: {
                  description:
                    "End of the time range to list custom shifts within. ISO 8601 with UTC offset, e.g. '2026-09-04T13:00:00Z'",
                  format: "date-time",
                  type: "string",
                },
                limit: {
                  default: 100,
                  description: "Maximum number of results to return",
                  maximum: 100,
                  minimum: 1,
                  type: "integer",
                },
                offset: {
                  anyOf: [
                    {
                      minimum: 0,
                      type: "integer",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Offset for pagination; use after a truncated=True response",
                },
              },
              required: ["schedule_id", "since", "until"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "get_custom_shift",
                  default: "get_custom_shift",
                  type: "string",
                },
                schedule_id: {
                  description:
                    "The ID of the schedule the custom shift belongs to",
                  type: "string",
                },
                custom_shift_id: {
                  description: "The ID of the custom shift to fetch",
                  type: "string",
                },
              },
              required: ["schedule_id", "custom_shift_id"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "list_overrides",
                  default: "list_overrides",
                  type: "string",
                },
                schedule_id: {
                  description: "The ID of the schedule to list overrides for",
                  type: "string",
                },
                since: {
                  description:
                    "Start of the time range to list overrides for. ISO 8601 with UTC offset, e.g. '2026-09-04T13:00:00Z'",
                  format: "date-time",
                  type: "string",
                },
                until: {
                  description:
                    "End of the time range to list overrides for. ISO 8601 with UTC offset, e.g. '2026-09-04T13:00:00Z'",
                  format: "date-time",
                  type: "string",
                },
                limit: {
                  default: 100,
                  description: "Maximum number of results to return",
                  maximum: 100,
                  minimum: 1,
                  type: "integer",
                },
                offset: {
                  anyOf: [
                    {
                      minimum: 0,
                      type: "integer",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Offset for pagination; use after a truncated=True response",
                },
              },
              required: ["schedule_id", "since", "until"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "get_override",
                  default: "get_override",
                  type: "string",
                },
                schedule_id: {
                  description: "The ID of the schedule the override belongs to",
                  type: "string",
                },
                override_id: {
                  description: "The ID of the override to fetch",
                  type: "string",
                },
              },
              required: ["schedule_id", "override_id"],
              type: "object",
            },
          ],
        },
      },
      required: ["request"],
      type: "object",
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
    },
  },
  {
    name: "browse_services",
    description:
      "Read PagerDuty service data.\n\nActions: list (filter by name/team), get (one service by ID).\n\nActions:\n- list: required=[], optional=[query, team_ids, limit, offset]\n- get: required=[service_id], optional=[]",
    inputSchema: {
      additionalProperties: false,
      properties: {
        request: {
          oneOf: [
            {
              properties: {
                action: {
                  const: "list",
                  default: "list",
                  type: "string",
                },
                query: {
                  anyOf: [
                    {
                      type: "string",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                },
                team_ids: {
                  anyOf: [
                    {
                      items: {
                        type: "string",
                      },
                      type: "array",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                },
                limit: {
                  default: 100,
                  description: "Maximum number of results to return",
                  maximum: 100,
                  minimum: 1,
                  type: "integer",
                },
                offset: {
                  anyOf: [
                    {
                      minimum: 0,
                      type: "integer",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Offset for pagination; use after a truncated=True response",
                },
              },
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "get",
                  default: "get",
                  type: "string",
                },
                service_id: {
                  type: "string",
                },
              },
              required: ["service_id"],
              type: "object",
            },
          ],
        },
      },
      required: ["request"],
      type: "object",
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
    },
  },
  {
    name: "browse_status_pages",
    description:
      "Read PagerDuty status page data.\n\nActions: list (all status pages, filter by type),\nlist_severities (severity levels for a page),\nlist_impacts (impact types for a page),\nlist_statuses (status values for a page),\nget_post (one post by page+post ID),\nlist_post_updates (updates on a post).\n\nActions:\n- list: required=[], optional=[status_page_type, limit, offset]\n- list_severities: required=[status_page_id], optional=[limit, offset]\n- list_impacts: required=[status_page_id], optional=[limit, offset]\n- list_statuses: required=[status_page_id], optional=[limit, offset]\n- get_post: required=[status_page_id, post_id], optional=[include]\n- list_post_updates: required=[status_page_id, post_id], optional=[limit, offset]",
    inputSchema: {
      additionalProperties: false,
      properties: {
        request: {
          oneOf: [
            {
              properties: {
                action: {
                  const: "list",
                  default: "list",
                  type: "string",
                },
                status_page_type: {
                  anyOf: [
                    {
                      enum: ["public", "private", "audience_specific"],
                      type: "string",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                },
                limit: {
                  default: 20,
                  description: "Maximum number of results to return",
                  maximum: 100,
                  minimum: 1,
                  type: "integer",
                },
                offset: {
                  anyOf: [
                    {
                      minimum: 0,
                      type: "integer",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Offset for pagination; use after a truncated=True response",
                },
              },
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "list_severities",
                  default: "list_severities",
                  type: "string",
                },
                status_page_id: {
                  type: "string",
                },
                limit: {
                  default: 20,
                  description: "Maximum number of results to return",
                  maximum: 100,
                  minimum: 1,
                  type: "integer",
                },
                offset: {
                  anyOf: [
                    {
                      minimum: 0,
                      type: "integer",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Offset for pagination; use after a truncated=True response",
                },
              },
              required: ["status_page_id"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "list_impacts",
                  default: "list_impacts",
                  type: "string",
                },
                status_page_id: {
                  type: "string",
                },
                limit: {
                  default: 20,
                  description: "Maximum number of results to return",
                  maximum: 100,
                  minimum: 1,
                  type: "integer",
                },
                offset: {
                  anyOf: [
                    {
                      minimum: 0,
                      type: "integer",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Offset for pagination; use after a truncated=True response",
                },
              },
              required: ["status_page_id"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "list_statuses",
                  default: "list_statuses",
                  type: "string",
                },
                status_page_id: {
                  type: "string",
                },
                limit: {
                  default: 20,
                  description: "Maximum number of results to return",
                  maximum: 100,
                  minimum: 1,
                  type: "integer",
                },
                offset: {
                  anyOf: [
                    {
                      minimum: 0,
                      type: "integer",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Offset for pagination; use after a truncated=True response",
                },
              },
              required: ["status_page_id"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "get_post",
                  default: "get_post",
                  type: "string",
                },
                status_page_id: {
                  type: "string",
                },
                post_id: {
                  type: "string",
                },
                include: {
                  anyOf: [
                    {
                      items: {
                        const: "status_page_post_update",
                        type: "string",
                      },
                      type: "array",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                },
              },
              required: ["status_page_id", "post_id"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "list_post_updates",
                  default: "list_post_updates",
                  type: "string",
                },
                status_page_id: {
                  type: "string",
                },
                post_id: {
                  type: "string",
                },
                limit: {
                  default: 20,
                  description: "Maximum number of results to return",
                  maximum: 100,
                  minimum: 1,
                  type: "integer",
                },
                offset: {
                  anyOf: [
                    {
                      minimum: 0,
                      type: "integer",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Offset for pagination; use after a truncated=True response",
                },
              },
              required: ["status_page_id", "post_id"],
              type: "object",
            },
          ],
        },
      },
      required: ["request"],
      type: "object",
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
    },
  },
  {
    name: "browse_teams",
    description:
      "Read PagerDuty team data.\n\nActions: list (filter by scope/name), get (one team by ID),\nlist_members (users in a team).\n\nActions:\n- list: required=[], optional=[scope, query, limit, offset]\n- get: required=[team_id], optional=[]\n- list_members: required=[team_id], optional=[limit, offset]",
    inputSchema: {
      additionalProperties: false,
      properties: {
        request: {
          oneOf: [
            {
              properties: {
                action: {
                  const: "list",
                  default: "list",
                  type: "string",
                },
                scope: {
                  anyOf: [
                    {
                      enum: ["all", "my"],
                      type: "string",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                },
                query: {
                  anyOf: [
                    {
                      type: "string",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                },
                limit: {
                  default: 100,
                  description: "Maximum number of results to return",
                  maximum: 100,
                  minimum: 1,
                  type: "integer",
                },
                offset: {
                  anyOf: [
                    {
                      minimum: 0,
                      type: "integer",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Offset for pagination (ignored when scope='my'); use after a truncated=True response",
                },
              },
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "get",
                  default: "get",
                  type: "string",
                },
                team_id: {
                  type: "string",
                },
              },
              required: ["team_id"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "list_members",
                  default: "list_members",
                  type: "string",
                },
                team_id: {
                  type: "string",
                },
                limit: {
                  default: 100,
                  description: "Maximum number of results to return",
                  maximum: 100,
                  minimum: 1,
                  type: "integer",
                },
                offset: {
                  anyOf: [
                    {
                      minimum: 0,
                      type: "integer",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Offset for pagination; use after a truncated=True response",
                },
              },
              required: ["team_id"],
              type: "object",
            },
          ],
        },
      },
      required: ["request"],
      type: "object",
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
    },
  },
  {
    name: "browse_users",
    description:
      "Read PagerDuty user data.\n\nActions: get (current authenticated user's profile),\nlist (search users by name or team IDs).\n\nActions:\n- get: required=[], optional=[]\n- list: required=[], optional=[query, team_ids, limit, offset]",
    inputSchema: {
      additionalProperties: false,
      properties: {
        request: {
          oneOf: [
            {
              properties: {
                action: {
                  const: "get",
                  default: "get",
                  type: "string",
                },
              },
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "list",
                  default: "list",
                  type: "string",
                },
                query: {
                  anyOf: [
                    {
                      type: "string",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                },
                team_ids: {
                  anyOf: [
                    {
                      items: {
                        type: "string",
                      },
                      type: "array",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                },
                limit: {
                  default: 100,
                  description: "Maximum number of results to return",
                  maximum: 100,
                  minimum: 1,
                  type: "integer",
                },
                offset: {
                  anyOf: [
                    {
                      minimum: 0,
                      type: "integer",
                    },
                    {
                      type: "null",
                    },
                  ],
                  default: null,
                  description:
                    "Offset for pagination; use after a truncated=True response",
                },
              },
              type: "object",
            },
          ],
        },
      },
      required: ["request"],
      type: "object",
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
    },
  },
  {
    name: "manage_alert_grouping",
    description:
      "Create, update, or delete PagerDuty alert grouping settings.\n\nActions: create (new setting), update (modify an existing setting),\ndelete (remove a setting by ID).\n\nActions:\n- create: required=[create_model.alert_grouping_setting], optional=[]\n- update: required=[setting_id, update_model.alert_grouping_setting], optional=[]\n- delete: required=[setting_id], optional=[]",
    inputSchema: {
      additionalProperties: false,
      properties: {
        request: {
          oneOf: [
            {
              properties: {
                action: {
                  const: "create",
                  default: "create",
                  type: "string",
                },
                create_model: {
                  description:
                    "Request wrapper for creating an alert grouping setting.",
                  properties: {
                    alert_grouping_setting: {
                      description: "The alert grouping setting to create",
                      properties: {
                        name: {
                          anyOf: [
                            {
                              type: "string",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description:
                            "An optional short-form string that provides succinct information about an AlertGroupingSetting object suitable for primary labeling of the entity.",
                        },
                        description: {
                          anyOf: [
                            {
                              type: "string",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description:
                            "An optional description that provides more information about an AlertGroupingSetting object.",
                        },
                        type: {
                          description:
                            "The alert grouping algorithm: 'content_based' groups alerts on an exact match of the chosen fields, best for predictable, homogeneous alert data; 'intelligent' groups alerts using ML-based textual similarity instead of exact matches, adapting over time; 'content_based_intelligent' (Unified Alert Grouping) requires both an exact field match and intelligent similarity before grouping; 'time' groups all alerts that arrive within a rolling time window, regardless of content.",
                          enum: [
                            "content_based",
                            "content_based_intelligent",
                            "intelligent",
                            "time",
                          ],
                          type: "string",
                        },
                        config: {
                          anyOf: [
                            {
                              description:
                                "Configuration for Content Based Alert Grouping.",
                              properties: {
                                aggregate: {
                                  description:
                                    "Whether Alerts should be grouped if 'all' or 'any' specified fields match. If 'all' is selected, an exact match on every specified field name must occur for Alerts to be grouped. If 'any' is selected, Alerts will be grouped when there is an exact match on at least one of the specified fields.",
                                  enum: ["all", "any"],
                                  type: "string",
                                },
                                fields: {
                                  description:
                                    "An array of strings which represent the fields with which to group against. Depending on the aggregate, Alerts will group if some or all the fields match.",
                                  items: {
                                    type: "string",
                                  },
                                  type: "array",
                                },
                                time_window: {
                                  description:
                                    "The maximum amount of time allowed between Alerts. Any Alerts arriving greater than `time_window` seconds apart will not be grouped together. This is a rolling time window up to 24 hours and is counted from the most recently grouped alert. To use the 'recommended_time_window,' set the value to 0, otherwise the value must be between 300 <= time_window <= 3600 or 86400 (i.e. 24 hours).",
                                  maximum: 86400,
                                  minimum: 300,
                                  type: "integer",
                                },
                                recommended_time_window: {
                                  anyOf: [
                                    {
                                      type: "integer",
                                    },
                                    {
                                      type: "null",
                                    },
                                  ],
                                  default: null,
                                  description:
                                    "In order to ensure your Service has the optimal grouping window, we use data science to calculate your Service's average Alert inter-arrival time. We encourage customer's to use this value, please set `time_window` to 0 to use the `recommended_time_window`.",
                                },
                              },
                              required: ["aggregate", "fields", "time_window"],
                              type: "object",
                            },
                            {
                              description:
                                "Configuration for Content Based Intelligent Alert Grouping.",
                              properties: {
                                aggregate: {
                                  description:
                                    "Whether Alerts should be grouped if 'all' or 'any' specified fields match. If 'all' is selected, an exact match on every specified field name must occur for Alerts to be grouped. If 'any' is selected, Alerts will be grouped when there is an exact match on at least one of the specified fields.",
                                  enum: ["all", "any"],
                                  type: "string",
                                },
                                fields: {
                                  description:
                                    "An array of strings which represent the fields with which to group against. Depending on the aggregate, Alerts will group if some or all the fields match.",
                                  items: {
                                    type: "string",
                                  },
                                  type: "array",
                                },
                                time_window: {
                                  description:
                                    "The maximum amount of time allowed between Alerts. Any Alerts arriving greater than `time_window` seconds apart will not be grouped together. This is a rolling time window up to 24 hours and is counted from the most recently grouped alert. To use the 'recommended_time_window,' set the value to 0, otherwise the value must be between 300 <= time_window <= 3600.",
                                  maximum: 3600,
                                  minimum: 300,
                                  type: "integer",
                                },
                                recommended_time_window: {
                                  anyOf: [
                                    {
                                      type: "integer",
                                    },
                                    {
                                      type: "null",
                                    },
                                  ],
                                  default: null,
                                  description:
                                    "In order to ensure your Service has the optimal grouping window, we use data science to calculate your Service's average Alert inter-arrival time. We encourage customer's to use this value, please set `time_window` to 0 to use the `recommended_time_window`.",
                                },
                              },
                              required: ["aggregate", "fields", "time_window"],
                              type: "object",
                            },
                            {
                              description:
                                "Configuration for Time Based Alert Grouping.",
                              properties: {
                                timeout: {
                                  description:
                                    "The duration in seconds within which to automatically group incoming Alerts. To continue grouping Alerts until the Incident is resolved, set this value to 0.",
                                  maximum: 86400,
                                  minimum: 60,
                                  type: "integer",
                                },
                              },
                              required: ["timeout"],
                              type: "object",
                            },
                            {
                              description:
                                "Configuration for Intelligent Alert Grouping.",
                              properties: {
                                time_window: {
                                  description:
                                    "The maximum amount of time allowed between Alerts. Any Alerts arriving greater than `time_window` seconds apart will not be grouped together. This is a rolling time window up to 24 hours and is counted from the most recently grouped alert. To use the 'recommended_time_window,' set the value to 0, otherwise the value must be between 300 <= time_window <= 3600.",
                                  maximum: 3600,
                                  minimum: 300,
                                  type: "integer",
                                },
                                recommended_time_window: {
                                  anyOf: [
                                    {
                                      type: "integer",
                                    },
                                    {
                                      type: "null",
                                    },
                                  ],
                                  default: null,
                                  description:
                                    "In order to ensure your Service has the optimal grouping window, we use data science to calculate your Service's average Alert inter-arrival time. We encourage customer's to use this value, please set `time_window` to 0 to use the `recommended_time_window`.",
                                },
                                iag_fields: {
                                  default: ["summary"],
                                  description:
                                    "An array of strings which represent the iag fields with which to intelligently group against.",
                                  items: {
                                    type: "string",
                                  },
                                  type: "array",
                                },
                              },
                              required: ["time_window"],
                              type: "object",
                            },
                          ],
                          description:
                            "The configuration for the alert grouping setting based on the type",
                        },
                        services: {
                          description:
                            "The array of one or many Services that the AlertGroupingSetting applies to. Type of content_based_intelligent allows for only one service in the array.",
                          items: {
                            description: "Reference to a service.",
                            properties: {
                              id: {
                                description: "The ID of the referenced object",
                                type: "string",
                              },
                              summary: {
                                anyOf: [
                                  {
                                    type: "string",
                                  },
                                  {
                                    type: "null",
                                  },
                                ],
                                default: null,
                                description:
                                  "A short-form, server-generated string that provides succinct information about the referenced object",
                              },
                            },
                            required: ["id"],
                            type: "object",
                          },
                          type: "array",
                        },
                      },
                      required: ["type", "config", "services"],
                      type: "object",
                    },
                  },
                  required: ["alert_grouping_setting"],
                  type: "object",
                },
              },
              required: ["create_model"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "update",
                  default: "update",
                  type: "string",
                },
                setting_id: {
                  type: "string",
                },
                update_model: {
                  description:
                    "Request wrapper for updating an alert grouping setting.",
                  properties: {
                    alert_grouping_setting: {
                      description:
                        "The alert grouping setting updates to apply",
                      properties: {
                        name: {
                          anyOf: [
                            {
                              type: "string",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description:
                            "An optional short-form string that provides succinct information about an AlertGroupingSetting object suitable for primary labeling of the entity.",
                        },
                        description: {
                          anyOf: [
                            {
                              type: "string",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description:
                            "An optional description that provides more information about an AlertGroupingSetting object.",
                        },
                        type: {
                          description:
                            "The alert grouping algorithm: 'content_based' groups alerts on an exact match of the chosen fields, best for predictable, homogeneous alert data; 'intelligent' groups alerts using ML-based textual similarity instead of exact matches, adapting over time; 'content_based_intelligent' (Unified Alert Grouping) requires both an exact field match and intelligent similarity before grouping; 'time' groups all alerts that arrive within a rolling time window, regardless of content.",
                          enum: [
                            "content_based",
                            "content_based_intelligent",
                            "intelligent",
                            "time",
                          ],
                          type: "string",
                        },
                        config: {
                          anyOf: [
                            {
                              description:
                                "Configuration for Content Based Alert Grouping.",
                              properties: {
                                aggregate: {
                                  description:
                                    "Whether Alerts should be grouped if 'all' or 'any' specified fields match. If 'all' is selected, an exact match on every specified field name must occur for Alerts to be grouped. If 'any' is selected, Alerts will be grouped when there is an exact match on at least one of the specified fields.",
                                  enum: ["all", "any"],
                                  type: "string",
                                },
                                fields: {
                                  description:
                                    "An array of strings which represent the fields with which to group against. Depending on the aggregate, Alerts will group if some or all the fields match.",
                                  items: {
                                    type: "string",
                                  },
                                  type: "array",
                                },
                                time_window: {
                                  description:
                                    "The maximum amount of time allowed between Alerts. Any Alerts arriving greater than `time_window` seconds apart will not be grouped together. This is a rolling time window up to 24 hours and is counted from the most recently grouped alert. To use the 'recommended_time_window,' set the value to 0, otherwise the value must be between 300 <= time_window <= 3600 or 86400 (i.e. 24 hours).",
                                  maximum: 86400,
                                  minimum: 300,
                                  type: "integer",
                                },
                                recommended_time_window: {
                                  anyOf: [
                                    {
                                      type: "integer",
                                    },
                                    {
                                      type: "null",
                                    },
                                  ],
                                  default: null,
                                  description:
                                    "In order to ensure your Service has the optimal grouping window, we use data science to calculate your Service's average Alert inter-arrival time. We encourage customer's to use this value, please set `time_window` to 0 to use the `recommended_time_window`.",
                                },
                              },
                              required: ["aggregate", "fields", "time_window"],
                              type: "object",
                            },
                            {
                              description:
                                "Configuration for Content Based Intelligent Alert Grouping.",
                              properties: {
                                aggregate: {
                                  description:
                                    "Whether Alerts should be grouped if 'all' or 'any' specified fields match. If 'all' is selected, an exact match on every specified field name must occur for Alerts to be grouped. If 'any' is selected, Alerts will be grouped when there is an exact match on at least one of the specified fields.",
                                  enum: ["all", "any"],
                                  type: "string",
                                },
                                fields: {
                                  description:
                                    "An array of strings which represent the fields with which to group against. Depending on the aggregate, Alerts will group if some or all the fields match.",
                                  items: {
                                    type: "string",
                                  },
                                  type: "array",
                                },
                                time_window: {
                                  description:
                                    "The maximum amount of time allowed between Alerts. Any Alerts arriving greater than `time_window` seconds apart will not be grouped together. This is a rolling time window up to 24 hours and is counted from the most recently grouped alert. To use the 'recommended_time_window,' set the value to 0, otherwise the value must be between 300 <= time_window <= 3600.",
                                  maximum: 3600,
                                  minimum: 300,
                                  type: "integer",
                                },
                                recommended_time_window: {
                                  anyOf: [
                                    {
                                      type: "integer",
                                    },
                                    {
                                      type: "null",
                                    },
                                  ],
                                  default: null,
                                  description:
                                    "In order to ensure your Service has the optimal grouping window, we use data science to calculate your Service's average Alert inter-arrival time. We encourage customer's to use this value, please set `time_window` to 0 to use the `recommended_time_window`.",
                                },
                              },
                              required: ["aggregate", "fields", "time_window"],
                              type: "object",
                            },
                            {
                              description:
                                "Configuration for Time Based Alert Grouping.",
                              properties: {
                                timeout: {
                                  description:
                                    "The duration in seconds within which to automatically group incoming Alerts. To continue grouping Alerts until the Incident is resolved, set this value to 0.",
                                  maximum: 86400,
                                  minimum: 60,
                                  type: "integer",
                                },
                              },
                              required: ["timeout"],
                              type: "object",
                            },
                            {
                              description:
                                "Configuration for Intelligent Alert Grouping.",
                              properties: {
                                time_window: {
                                  description:
                                    "The maximum amount of time allowed between Alerts. Any Alerts arriving greater than `time_window` seconds apart will not be grouped together. This is a rolling time window up to 24 hours and is counted from the most recently grouped alert. To use the 'recommended_time_window,' set the value to 0, otherwise the value must be between 300 <= time_window <= 3600.",
                                  maximum: 3600,
                                  minimum: 300,
                                  type: "integer",
                                },
                                recommended_time_window: {
                                  anyOf: [
                                    {
                                      type: "integer",
                                    },
                                    {
                                      type: "null",
                                    },
                                  ],
                                  default: null,
                                  description:
                                    "In order to ensure your Service has the optimal grouping window, we use data science to calculate your Service's average Alert inter-arrival time. We encourage customer's to use this value, please set `time_window` to 0 to use the `recommended_time_window`.",
                                },
                                iag_fields: {
                                  default: ["summary"],
                                  description:
                                    "An array of strings which represent the iag fields with which to intelligently group against.",
                                  items: {
                                    type: "string",
                                  },
                                  type: "array",
                                },
                              },
                              required: ["time_window"],
                              type: "object",
                            },
                          ],
                          description:
                            "The configuration for the alert grouping setting based on the type",
                        },
                        services: {
                          description:
                            "The array of one or many Services that the AlertGroupingSetting applies to. Type of content_based_intelligent allows for only one service in the array.",
                          items: {
                            description: "Reference to a service.",
                            properties: {
                              id: {
                                description: "The ID of the referenced object",
                                type: "string",
                              },
                              summary: {
                                anyOf: [
                                  {
                                    type: "string",
                                  },
                                  {
                                    type: "null",
                                  },
                                ],
                                default: null,
                                description:
                                  "A short-form, server-generated string that provides succinct information about the referenced object",
                              },
                            },
                            required: ["id"],
                            type: "object",
                          },
                          type: "array",
                        },
                      },
                      required: ["type", "config", "services"],
                      type: "object",
                    },
                  },
                  required: ["alert_grouping_setting"],
                  type: "object",
                },
              },
              required: ["setting_id", "update_model"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "delete",
                  default: "delete",
                  type: "string",
                },
                setting_id: {
                  type: "string",
                },
              },
              required: ["setting_id"],
              type: "object",
            },
          ],
        },
      },
      required: ["request"],
      type: "object",
    },
    annotations: {
      readOnlyHint: false,
      destructiveHint: true,
      idempotentHint: false,
    },
  },
  {
    name: "manage_event_orchestrations",
    description:
      "Modify PagerDuty event orchestration routing rules.\n\nActions: update_router (replace the full router config),\nappend_router_rule (add a new rule to the end of the router).\n\nActions:\n- update_router: required=[orchestration_id, router_update.orchestration_path], optional=[]\n- append_router_rule: required=[orchestration_id, new_rule.conditions, new_rule.actions], optional=[new_rule.label, new_rule.disabled]",
    inputSchema: {
      additionalProperties: false,
      properties: {
        request: {
          oneOf: [
            {
              properties: {
                action: {
                  const: "update_router",
                  default: "update_router",
                  type: "string",
                },
                orchestration_id: {
                  type: "string",
                },
                router_update: {
                  description:
                    "Request model for updating an event orchestration router configuration.",
                  properties: {
                    orchestration_path: {
                      description:
                        "The orchestration router path configuration to update",
                      properties: {
                        type: {
                          const: "router",
                          default: "router",
                          description:
                            "Indicates that these are a 'router' type set of rules.",
                          type: "string",
                        },
                        sets: {
                          description:
                            "The Router contains a single set of rules (the 'start' set). The Router evaluates Events against these Rules, one at a time, and routes each Event to a specific Service based on the first rule that matches.",
                          items: {
                            properties: {
                              id: {
                                default: "start",
                                description:
                                  "The ID of this set of rules. Rules in other sets can route events into this set using the 'route_to' properties.",
                                type: "string",
                              },
                              rules: {
                                description: "List of rules in this set",
                                items: {
                                  properties: {
                                    id: {
                                      description: "ID of the rule",
                                      readOnly: true,
                                      type: "string",
                                    },
                                    label: {
                                      anyOf: [
                                        {
                                          type: "string",
                                        },
                                        {
                                          type: "null",
                                        },
                                      ],
                                      default: null,
                                      description:
                                        "A description of this rule's purpose.",
                                    },
                                    conditions: {
                                      description:
                                        "Each of these conditions is evaluated to check if an event matches this rule. The rule is considered a match if **any** of these conditions match.",
                                      items: {
                                        properties: {
                                          expression: {
                                            description:
                                              "A PCL condition string",
                                            example:
                                              "event.summary matches part 'my service error'",
                                            type: "string",
                                          },
                                        },
                                        required: ["expression"],
                                        type: "object",
                                      },
                                      type: "array",
                                    },
                                    actions: {
                                      properties: {
                                        route_to: {
                                          anyOf: [
                                            {
                                              type: "string",
                                            },
                                            {
                                              type: "null",
                                            },
                                          ],
                                          default: null,
                                          description:
                                            "The ID of the target Service for the resulting alert. You can find the service you want to route to by calling the services endpoint.",
                                          example: "PSI2I2O",
                                        },
                                        dynamic_route_to: {
                                          anyOf: [
                                            {
                                              additionalProperties: true,
                                              type: "object",
                                            },
                                            {
                                              type: "null",
                                            },
                                          ],
                                          default: null,
                                          description:
                                            "Use the contents of an event payload to dynamically route an event to the target service. Available to AIOps customers.",
                                        },
                                      },
                                      type: "object",
                                      description:
                                        "When an event matches this rule, these are the actions that will be taken to change the resulting alert and incident.",
                                    },
                                    disabled: {
                                      anyOf: [
                                        {
                                          type: "boolean",
                                        },
                                        {
                                          type: "null",
                                        },
                                      ],
                                      default: false,
                                      description:
                                        "Indicates whether the rule is disabled and would therefore not be evaluated.",
                                    },
                                  },
                                  required: ["id", "conditions", "actions"],
                                  type: "object",
                                },
                                type: "array",
                              },
                            },
                            required: ["rules"],
                            type: "object",
                          },
                          maxItems: 1,
                          type: "array",
                        },
                        catch_all: {
                          properties: {
                            actions: {
                              properties: {
                                route_to: {
                                  anyOf: [
                                    {
                                      type: "string",
                                    },
                                    {
                                      type: "null",
                                    },
                                  ],
                                  default: null,
                                  description:
                                    "The ID of the target Service for the resulting alert. You can find the service you want to route to by calling the services endpoint.",
                                  example: "PSI2I2O",
                                },
                                dynamic_route_to: {
                                  anyOf: [
                                    {
                                      additionalProperties: true,
                                      type: "object",
                                    },
                                    {
                                      type: "null",
                                    },
                                  ],
                                  default: null,
                                  description:
                                    "Use the contents of an event payload to dynamically route an event to the target service. Available to AIOps customers.",
                                },
                              },
                              type: "object",
                              description:
                                "These are the actions that will be taken to change the resulting alert and incident.",
                            },
                          },
                          required: ["actions"],
                          type: "object",
                          description:
                            "When none of the rules match an event, the event will be routed according to the catch_all settings.",
                        },
                      },
                      required: ["sets", "catch_all"],
                      type: "object",
                    },
                  },
                  required: ["orchestration_path"],
                  type: "object",
                },
              },
              required: ["orchestration_id", "router_update"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "append_router_rule",
                  default: "append_router_rule",
                  type: "string",
                },
                orchestration_id: {
                  type: "string",
                },
                new_rule: {
                  description:
                    "Request model for creating a new event orchestration rule.",
                  properties: {
                    label: {
                      anyOf: [
                        {
                          type: "string",
                        },
                        {
                          type: "null",
                        },
                      ],
                      default: null,
                      description: "A description of this rule's purpose.",
                    },
                    conditions: {
                      description:
                        "Each of these conditions is evaluated to check if an event matches this rule. The rule is considered a match if **any** of these conditions match.",
                      items: {
                        properties: {
                          expression: {
                            description: "A PCL condition string",
                            example:
                              "event.summary matches part 'my service error'",
                            type: "string",
                          },
                        },
                        required: ["expression"],
                        type: "object",
                      },
                      type: "array",
                    },
                    actions: {
                      properties: {
                        route_to: {
                          anyOf: [
                            {
                              type: "string",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description:
                            "The ID of the target Service for the resulting alert. You can find the service you want to route to by calling the services endpoint.",
                          example: "PSI2I2O",
                        },
                        dynamic_route_to: {
                          anyOf: [
                            {
                              additionalProperties: true,
                              type: "object",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description:
                            "Use the contents of an event payload to dynamically route an event to the target service. Available to AIOps customers.",
                        },
                      },
                      type: "object",
                      description:
                        "When an event matches this rule, these are the actions that will be taken to change the resulting alert and incident.",
                    },
                    disabled: {
                      anyOf: [
                        {
                          type: "boolean",
                        },
                        {
                          type: "null",
                        },
                      ],
                      default: false,
                      description:
                        "Indicates whether the rule is disabled and would therefore not be evaluated.",
                    },
                  },
                  required: ["conditions", "actions"],
                  type: "object",
                },
              },
              required: ["orchestration_id", "new_rule"],
              type: "object",
            },
          ],
        },
      },
      required: ["request"],
      type: "object",
    },
    annotations: {
      readOnlyHint: false,
      destructiveHint: true,
      idempotentHint: false,
    },
  },
  {
    name: "manage_incidents",
    description:
      "Modify PagerDuty incidents.\n\nActions: create (new incident), update (change status/urgency/assignment/escalation),\nadd_note (append a note), add_responders (page additional responders),\nstart_workflow (trigger an incident workflow).\n\nActions:\n- create: required=[incident.title, incident.service], optional=[incident.urgency, incident.body, incident.assignments]\n- update: required=[manage_request.incident_ids], optional=[manage_request.assignment, manage_request.status, manage_request.urgency, manage_request.escalation_level]\n- add_note: required=[incident_id, note], optional=[]\n- add_responders: required=[incident_id, request.message, request.responder_request_targets], optional=[]\n- start_workflow: required=[workflow_id, instance_request.incident_workflow_instance], optional=[]",
    inputSchema: {
      additionalProperties: false,
      properties: {
        request: {
          oneOf: [
            {
              properties: {
                action: {
                  const: "create",
                  default: "create",
                  type: "string",
                },
                incident: {
                  properties: {
                    title: {
                      description: "The title of the incident",
                      type: "string",
                    },
                    service: {
                      description: "The service associated with the incident",
                      properties: {
                        id: {
                          description: "The ID of the referenced object",
                          type: "string",
                        },
                        summary: {
                          anyOf: [
                            {
                              type: "string",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description:
                            "A short-form, server-generated string that provides succinct information about the referenced object",
                        },
                      },
                      required: ["id"],
                      type: "object",
                    },
                    urgency: {
                      anyOf: [
                        {
                          enum: ["high", "low"],
                          type: "string",
                        },
                        {
                          type: "null",
                        },
                      ],
                      default: "high",
                      description: "The urgency of the incident",
                    },
                    body: {
                      anyOf: [
                        {
                          properties: {
                            details: {
                              description: "The details of the incident body",
                              type: "string",
                            },
                          },
                          required: ["details"],
                          type: "object",
                        },
                        {
                          type: "null",
                        },
                      ],
                      default: null,
                      description:
                        "The body of the incident. This is a free-form text field that can be used to provide additional details about the incident.",
                    },
                    assignments: {
                      anyOf: [
                        {
                          items: {
                            description:
                              "Assignment for creating/updating incidents (no 'at' field required).",
                            properties: {
                              assignee: {
                                description:
                                  "The user to assign to the incident",
                                properties: {
                                  id: {
                                    description:
                                      "The ID of the referenced object",
                                    type: "string",
                                  },
                                  summary: {
                                    anyOf: [
                                      {
                                        type: "string",
                                      },
                                      {
                                        type: "null",
                                      },
                                    ],
                                    default: null,
                                    description:
                                      "A short-form, server-generated string that provides succinct information about the referenced object",
                                  },
                                },
                                required: ["id"],
                                type: "object",
                              },
                            },
                            required: ["assignee"],
                            type: "object",
                          },
                          type: "array",
                        },
                        {
                          type: "null",
                        },
                      ],
                      default: null,
                      description:
                        "List of assignees for the incident. Each assignment requires an 'assignee' with 'id' (user ID) and 'type' set to 'user_reference'. When provided, only the assigned user(s) receive the initial notification instead of the default service owner.",
                    },
                  },
                  required: ["title", "service"],
                  type: "object",
                },
              },
              required: ["incident"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "update",
                  default: "update",
                  type: "string",
                },
                manage_request: {
                  properties: {
                    incident_ids: {
                      description: "The ID of the incidents to manage",
                      items: {
                        type: "string",
                      },
                      type: "array",
                    },
                    assignment: {
                      anyOf: [
                        {
                          description: "Reference to a user.",
                          properties: {
                            id: {
                              description: "The ID of the referenced object",
                              type: "string",
                            },
                            summary: {
                              anyOf: [
                                {
                                  type: "string",
                                },
                                {
                                  type: "null",
                                },
                              ],
                              default: null,
                              description:
                                "A short-form, server-generated string that provides succinct information about the referenced object",
                            },
                          },
                          required: ["id"],
                          type: "object",
                        },
                        {
                          type: "null",
                        },
                      ],
                      default: null,
                      description: "The user to assign the incident to",
                    },
                    status: {
                      anyOf: [
                        {
                          enum: ["acknowledged", "resolved"],
                          type: "string",
                        },
                        {
                          type: "null",
                        },
                      ],
                      default: null,
                      description: "The status to set the incident to",
                    },
                    urgency: {
                      anyOf: [
                        {
                          enum: ["high", "low"],
                          type: "string",
                        },
                        {
                          type: "null",
                        },
                      ],
                      default: null,
                      description: "The priority to set the incident to",
                    },
                    escalation_level: {
                      anyOf: [
                        {
                          type: "integer",
                        },
                        {
                          type: "null",
                        },
                      ],
                      default: null,
                      description:
                        "The escalation level to set the incident to",
                    },
                  },
                  required: ["incident_ids"],
                  type: "object",
                },
              },
              required: ["manage_request"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "add_note",
                  default: "add_note",
                  type: "string",
                },
                incident_id: {
                  type: "string",
                },
                note: {
                  type: "string",
                },
              },
              required: ["incident_id", "note"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "add_responders",
                  default: "add_responders",
                  type: "string",
                },
                incident_id: {
                  type: "string",
                },
                request: {
                  properties: {
                    message: {
                      description:
                        "Optional message to include with the responder request",
                      type: "string",
                    },
                    responder_request_targets: {
                      description:
                        "Array of user or escalation policy IDs to request as responders",
                      items: {
                        properties: {
                          responder_request_target: {
                            properties: {
                              id: {
                                description:
                                  "The ID of the user or escalation policy to request as a responder",
                                type: "string",
                              },
                              type: {
                                description:
                                  "The type of target (either a user or an escalation policy)",
                                enum: [
                                  "user_reference",
                                  "escalation_policy_reference",
                                ],
                                type: "string",
                              },
                            },
                            required: ["id", "type"],
                            type: "object",
                            description:
                              "Array of user or escalation policy IDs to request as responders",
                          },
                        },
                        required: ["responder_request_target"],
                        type: "object",
                      },
                      type: "array",
                    },
                  },
                  required: ["message", "responder_request_targets"],
                  type: "object",
                },
              },
              required: ["incident_id", "request"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "start_workflow",
                  default: "start_workflow",
                  type: "string",
                },
                workflow_id: {
                  type: "string",
                },
                instance_request: {
                  description:
                    "Wrapper for incident workflow instance creation request.",
                  properties: {
                    incident_workflow_instance: {
                      description: "The incident workflow instance to create",
                      properties: {
                        id: {
                          anyOf: [
                            {
                              type: "string",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description:
                            "An identifier to help differentiate between workflow executions",
                        },
                        incident: {
                          description: "Reference to the incident",
                          properties: {
                            id: {
                              description: "The ID of the referenced object",
                              type: "string",
                            },
                            summary: {
                              anyOf: [
                                {
                                  type: "string",
                                },
                                {
                                  type: "null",
                                },
                              ],
                              default: null,
                              description:
                                "A short-form, server-generated string that provides succinct information about the referenced object",
                            },
                          },
                          required: ["id"],
                          type: "object",
                        },
                      },
                      required: ["incident"],
                      type: "object",
                    },
                  },
                  required: ["incident_workflow_instance"],
                  type: "object",
                },
              },
              required: ["workflow_id", "instance_request"],
              type: "object",
            },
          ],
        },
      },
      required: ["request"],
      type: "object",
    },
    annotations: {
      readOnlyHint: false,
      destructiveHint: true,
      idempotentHint: false,
    },
  },
  {
    name: "manage_schedules",
    description:
      "Create, update or delete PagerDuty schedules and their shifts.\n\ncreate and update take schedule_data.kind 'legacy' (v2, layer-based) or 'shift_based' (v3).\nA shift_based schedule is created empty: add a rotation with create_rotation, then its\nshifts with create_rotation_event. create_override is legacy only; for shift_based\nschedules use create_overrides, which targets a rotation or custom shift. delete_schedule_v3\nand all rotation, rotation_event, custom_shift and override actions are shift_based only.\n\nActions:\n- create: required=[schedule_data], optional=[]\n- update: required=[schedule_id, schedule_data], optional=[]\n- create_override: required=[schedule_id, override_request.overrides], optional=[]\n- delete_schedule_v3: required=[schedule_id], optional=[]\n- create_rotation: required=[schedule_id], optional=[]\n- delete_rotation: required=[schedule_id, rotation_id], optional=[]\n- create_rotation_event: required=[schedule_id, rotation_id, event_data.name, event_data.start_time, event_data.end_time, event_data.effective_since, event_data.recurrence, event_data.assignment_strategy], optional=[event_data.effective_until]\n- update_rotation_event: required=[schedule_id, rotation_id, event_id, event_data.name, event_data.start_time, event_data.end_time, event_data.effective_since, event_data.recurrence, event_data.assignment_strategy], optional=[event_data.effective_until]\n- delete_rotation_event: required=[schedule_id, rotation_id, event_id], optional=[]\n- create_custom_shifts: required=[schedule_id, shifts], optional=[]\n- update_custom_shift: required=[schedule_id, custom_shift_id, shift_data.start_time, shift_data.end_time, shift_data.assignments], optional=[]\n- delete_custom_shift: required=[schedule_id, custom_shift_id], optional=[]\n- create_overrides: required=[schedule_id, overrides], optional=[]\n- update_override: required=[schedule_id, override_id, override_data.start_time, override_data.end_time, override_data.overridden_member, override_data.overriding_member], optional=[]\n- delete_override: required=[schedule_id, override_id], optional=[]",
    inputSchema: {
      additionalProperties: false,
      properties: {
        request: {
          oneOf: [
            {
              properties: {
                action: {
                  const: "create",
                  default: "create",
                  type: "string",
                },
                schedule_data: {
                  oneOf: [
                    {
                      properties: {
                        kind: {
                          const: "legacy",
                          description: "'legacy' for a v2 schedule",
                          type: "string",
                        },
                        schedule: {
                          properties: {
                            name: {
                              description: "The name of the schedule",
                              type: "string",
                            },
                            time_zone: {
                              description:
                                "The IANA time zone of the schedule, e.g. 'America/New_York'",
                              type: "string",
                            },
                            description: {
                              anyOf: [
                                {
                                  type: "string",
                                },
                                {
                                  type: "null",
                                },
                              ],
                              default: null,
                              description: "The description of the schedule",
                            },
                            schedule_layers: {
                              description: "A list of schedule layers",
                              items: {
                                properties: {
                                  name: {
                                    description:
                                      "The name of the schedule layer",
                                    type: "string",
                                  },
                                  start: {
                                    description:
                                      "The start time of this layer. ISO 8601, e.g. '2026-09-04T13:00:00Z'",
                                    format: "date-time",
                                    type: "string",
                                  },
                                  end: {
                                    anyOf: [
                                      {
                                        format: "date-time",
                                        type: "string",
                                      },
                                      {
                                        type: "null",
                                      },
                                    ],
                                    default: null,
                                    description:
                                      "The end time of this layer. If null, the layer does not end",
                                  },
                                  rotation_virtual_start: {
                                    description:
                                      "The effective start time of the layer. This can be before the start time of the schedule",
                                    format: "date-time",
                                    type: "string",
                                  },
                                  rotation_turn_length_seconds: {
                                    description:
                                      "The duration of each on-call shift in seconds",
                                    type: "integer",
                                  },
                                  users: {
                                    description:
                                      "The ordered list of users on this layer. The position of the user on the list determines their order in the layer",
                                    items: {
                                      properties: {
                                        user: {
                                          description:
                                            "The reference to the user in this layer",
                                          properties: {
                                            id: {
                                              description:
                                                "The ID of the referenced object",
                                              type: "string",
                                            },
                                            summary: {
                                              anyOf: [
                                                {
                                                  type: "string",
                                                },
                                                {
                                                  type: "null",
                                                },
                                              ],
                                              default: null,
                                              description:
                                                "A short-form, server-generated string that provides succinct information about the referenced object",
                                            },
                                          },
                                          required: ["id"],
                                          type: "object",
                                        },
                                      },
                                      required: ["user"],
                                      type: "object",
                                    },
                                    type: "array",
                                  },
                                  restrictions: {
                                    anyOf: [
                                      {
                                        items: {
                                          properties: {
                                            type: {
                                              description:
                                                "'daily_restriction': the layer is active every day, from start_time_of_day for duration_seconds. 'weekly_restriction': the layer is active once a week, from start_time_of_day on start_day_of_week for duration_seconds",
                                              enum: [
                                                "daily_restriction",
                                                "weekly_restriction",
                                              ],
                                              type: "string",
                                            },
                                            start_time_of_day: {
                                              description:
                                                "The time of day when the restriction starts (HH:MM:SS)",
                                              type: "string",
                                            },
                                            duration_seconds: {
                                              description:
                                                "The duration of the restriction in seconds",
                                              type: "integer",
                                            },
                                            start_day_of_week: {
                                              default: 1,
                                              description:
                                                "The day of week the restriction starts (1=Monday, 7=Sunday, ISO-8601)",
                                              type: "integer",
                                            },
                                          },
                                          required: [
                                            "type",
                                            "start_time_of_day",
                                            "duration_seconds",
                                          ],
                                          type: "object",
                                        },
                                        type: "array",
                                      },
                                      {
                                        type: "null",
                                      },
                                    ],
                                    default: null,
                                    description:
                                      "An array of restrictions for the layer. A restriction is a limit on which period of the day or week the schedule layer can accept assignments",
                                  },
                                },
                                required: [
                                  "name",
                                  "start",
                                  "rotation_virtual_start",
                                  "rotation_turn_length_seconds",
                                  "users",
                                ],
                                type: "object",
                              },
                              type: "array",
                            },
                          },
                          required: ["name", "time_zone", "schedule_layers"],
                          type: "object",
                          description: "The schedule to be created",
                        },
                      },
                      required: ["kind", "schedule"],
                      type: "object",
                    },
                    {
                      additionalProperties: false,
                      properties: {
                        kind: {
                          const: "shift_based",
                          description: "'shift_based' for a v3 schedule",
                          type: "string",
                        },
                        name: {
                          description: "The name of the schedule",
                          type: "string",
                        },
                        time_zone: {
                          description:
                            "The IANA time zone of the schedule, e.g. 'America/New_York'",
                          type: "string",
                        },
                        description: {
                          anyOf: [
                            {
                              type: "string",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description: "The description of the schedule",
                        },
                        teams: {
                          anyOf: [
                            {
                              items: {
                                description: "Reference to a team.",
                                properties: {
                                  id: {
                                    description:
                                      "The ID of the referenced object",
                                    type: "string",
                                  },
                                  summary: {
                                    anyOf: [
                                      {
                                        type: "string",
                                      },
                                      {
                                        type: "null",
                                      },
                                    ],
                                    default: null,
                                    description:
                                      "A short-form, server-generated string that provides succinct information about the referenced object",
                                  },
                                },
                                required: ["id"],
                                type: "object",
                              },
                              type: "array",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description:
                            "Teams to associate with this schedule. Rotations cannot be created here; add them afterwards with manage_schedules action 'create_rotation'",
                        },
                      },
                      required: ["kind", "name", "time_zone"],
                      type: "object",
                    },
                  ],
                },
              },
              required: ["schedule_data"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "update",
                  default: "update",
                  type: "string",
                },
                schedule_id: {
                  type: "string",
                },
                schedule_data: {
                  oneOf: [
                    {
                      properties: {
                        kind: {
                          const: "legacy",
                          description: "'legacy' for a v2 schedule",
                          type: "string",
                        },
                        schedule: {
                          properties: {
                            name: {
                              description: "The name of the schedule",
                              type: "string",
                            },
                            time_zone: {
                              description:
                                "The IANA time zone of the schedule, e.g. 'America/New_York'",
                              type: "string",
                            },
                            description: {
                              anyOf: [
                                {
                                  type: "string",
                                },
                                {
                                  type: "null",
                                },
                              ],
                              default: null,
                              description: "The description of the schedule",
                            },
                            schedule_layers: {
                              description: "A list of schedule layers",
                              items: {
                                properties: {
                                  name: {
                                    description:
                                      "The name of the schedule layer",
                                    type: "string",
                                  },
                                  start: {
                                    description:
                                      "The start time of this layer. ISO 8601, e.g. '2026-09-04T13:00:00Z'",
                                    format: "date-time",
                                    type: "string",
                                  },
                                  end: {
                                    anyOf: [
                                      {
                                        format: "date-time",
                                        type: "string",
                                      },
                                      {
                                        type: "null",
                                      },
                                    ],
                                    default: null,
                                    description:
                                      "The end time of this layer. If null, the layer does not end",
                                  },
                                  rotation_virtual_start: {
                                    description:
                                      "The effective start time of the layer. This can be before the start time of the schedule",
                                    format: "date-time",
                                    type: "string",
                                  },
                                  rotation_turn_length_seconds: {
                                    description:
                                      "The duration of each on-call shift in seconds",
                                    type: "integer",
                                  },
                                  users: {
                                    description:
                                      "The ordered list of users on this layer. The position of the user on the list determines their order in the layer",
                                    items: {
                                      properties: {
                                        user: {
                                          description:
                                            "The reference to the user in this layer",
                                          properties: {
                                            id: {
                                              description:
                                                "The ID of the referenced object",
                                              type: "string",
                                            },
                                            summary: {
                                              anyOf: [
                                                {
                                                  type: "string",
                                                },
                                                {
                                                  type: "null",
                                                },
                                              ],
                                              default: null,
                                              description:
                                                "A short-form, server-generated string that provides succinct information about the referenced object",
                                            },
                                          },
                                          required: ["id"],
                                          type: "object",
                                        },
                                      },
                                      required: ["user"],
                                      type: "object",
                                    },
                                    type: "array",
                                  },
                                  restrictions: {
                                    anyOf: [
                                      {
                                        items: {
                                          properties: {
                                            type: {
                                              description:
                                                "'daily_restriction': the layer is active every day, from start_time_of_day for duration_seconds. 'weekly_restriction': the layer is active once a week, from start_time_of_day on start_day_of_week for duration_seconds",
                                              enum: [
                                                "daily_restriction",
                                                "weekly_restriction",
                                              ],
                                              type: "string",
                                            },
                                            start_time_of_day: {
                                              description:
                                                "The time of day when the restriction starts (HH:MM:SS)",
                                              type: "string",
                                            },
                                            duration_seconds: {
                                              description:
                                                "The duration of the restriction in seconds",
                                              type: "integer",
                                            },
                                            start_day_of_week: {
                                              default: 1,
                                              description:
                                                "The day of week the restriction starts (1=Monday, 7=Sunday, ISO-8601)",
                                              type: "integer",
                                            },
                                          },
                                          required: [
                                            "type",
                                            "start_time_of_day",
                                            "duration_seconds",
                                          ],
                                          type: "object",
                                        },
                                        type: "array",
                                      },
                                      {
                                        type: "null",
                                      },
                                    ],
                                    default: null,
                                    description:
                                      "An array of restrictions for the layer. A restriction is a limit on which period of the day or week the schedule layer can accept assignments",
                                  },
                                },
                                required: [
                                  "name",
                                  "start",
                                  "rotation_virtual_start",
                                  "rotation_turn_length_seconds",
                                  "users",
                                ],
                                type: "object",
                              },
                              type: "array",
                            },
                          },
                          required: ["name", "time_zone", "schedule_layers"],
                          type: "object",
                          description: "The updated schedule data",
                        },
                      },
                      required: ["kind", "schedule"],
                      type: "object",
                    },
                    {
                      additionalProperties: false,
                      properties: {
                        kind: {
                          const: "shift_based",
                          description: "'shift_based' for a v3 schedule",
                          type: "string",
                        },
                        name: {
                          anyOf: [
                            {
                              type: "string",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description: "The name of the schedule",
                        },
                        time_zone: {
                          anyOf: [
                            {
                              type: "string",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description:
                            "The IANA time zone of the schedule, e.g. 'America/New_York'",
                        },
                        description: {
                          anyOf: [
                            {
                              type: "string",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description: "The description of the schedule",
                        },
                        teams: {
                          anyOf: [
                            {
                              items: {
                                description: "Reference to a team.",
                                properties: {
                                  id: {
                                    description:
                                      "The ID of the referenced object",
                                    type: "string",
                                  },
                                  summary: {
                                    anyOf: [
                                      {
                                        type: "string",
                                      },
                                      {
                                        type: "null",
                                      },
                                    ],
                                    default: null,
                                    description:
                                      "A short-form, server-generated string that provides succinct information about the referenced object",
                                  },
                                },
                                required: ["id"],
                                type: "object",
                              },
                              type: "array",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description: "Replacement teams for this schedule",
                        },
                      },
                      required: ["kind"],
                      type: "object",
                    },
                  ],
                },
              },
              required: ["schedule_id", "schedule_data"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "create_override",
                  default: "create_override",
                  type: "string",
                },
                schedule_id: {
                  type: "string",
                },
                override_request: {
                  properties: {
                    overrides: {
                      description:
                        "The list of overrides to create for the schedule",
                      items: {
                        properties: {
                          start: {
                            description: "The start time of the override",
                            format: "date-time",
                            type: "string",
                          },
                          end: {
                            description: "The end time of the override",
                            format: "date-time",
                            type: "string",
                          },
                          user_id: {
                            description: "The ID of the user for this override",
                            type: "string",
                          },
                        },
                        required: ["start", "end", "user_id"],
                        type: "object",
                      },
                      type: "array",
                    },
                  },
                  required: ["overrides"],
                  type: "object",
                },
              },
              required: ["schedule_id", "override_request"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "delete_schedule_v3",
                  default: "delete_schedule_v3",
                  type: "string",
                },
                schedule_id: {
                  type: "string",
                },
              },
              required: ["schedule_id"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "create_rotation",
                  default: "create_rotation",
                  type: "string",
                },
                schedule_id: {
                  description:
                    "The ID of the schedule to create the rotation on",
                  type: "string",
                },
              },
              required: ["schedule_id"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "delete_rotation",
                  default: "delete_rotation",
                  type: "string",
                },
                schedule_id: {
                  description: "The ID of the schedule the rotation belongs to",
                  type: "string",
                },
                rotation_id: {
                  description: "The ID of the rotation to delete",
                  type: "string",
                },
              },
              required: ["schedule_id", "rotation_id"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "create_rotation_event",
                  default: "create_rotation_event",
                  type: "string",
                },
                schedule_id: {
                  description: "The ID of the schedule the rotation belongs to",
                  type: "string",
                },
                rotation_id: {
                  description: "The ID of the rotation to create the event on",
                  type: "string",
                },
                event_data: {
                  additionalProperties: false,
                  properties: {
                    name: {
                      description: "The name of the rotation event",
                      type: "string",
                    },
                    start_time: {
                      properties: {
                        date_time: {
                          description:
                            "Date-time. ISO 8601 with UTC offset, e.g. '2026-09-04T13:00:00Z'",
                          format: "date-time",
                          type: "string",
                        },
                        time_zone: {
                          description:
                            "IANA time zone name, e.g. 'America/New_York'",
                          type: "string",
                        },
                      },
                      required: ["date_time", "time_zone"],
                      type: "object",
                      description: "The start time of the event",
                    },
                    end_time: {
                      properties: {
                        date_time: {
                          description:
                            "Date-time. ISO 8601 with UTC offset, e.g. '2026-09-04T13:00:00Z'",
                          format: "date-time",
                          type: "string",
                        },
                        time_zone: {
                          description:
                            "IANA time zone name, e.g. 'America/New_York'",
                          type: "string",
                        },
                      },
                      required: ["date_time", "time_zone"],
                      type: "object",
                      description: "The end time of the event",
                    },
                    effective_since: {
                      description:
                        "When the event takes effect; past values are clamped to now. ISO 8601 with UTC offset, e.g. '2026-09-04T13:00:00Z'",
                      format: "date-time",
                      type: "string",
                    },
                    effective_until: {
                      anyOf: [
                        {
                          format: "date-time",
                          type: "string",
                        },
                        {
                          type: "null",
                        },
                      ],
                      default: null,
                      description:
                        "When the event stops being effective; omit for indefinite. ISO 8601 with UTC offset, e.g. '2026-09-04T13:00:00Z'",
                    },
                    recurrence: {
                      description:
                        "RFC 5545 rules: exactly one RRULE (e.g. 'RRULE:FREQ=WEEKLY;BYDAY=MO,TU'), plus optional EXDATE/RDATE entries",
                      items: {
                        type: "string",
                      },
                      type: "array",
                    },
                    assignment_strategy: {
                      additionalProperties: false,
                      properties: {
                        type: {
                          description:
                            "'rotating_member_assignment_strategy' hands off to the next member after shifts_per_member shifts; 'every_member_assignment_strategy' puts all members on every shift",
                          enum: [
                            "rotating_member_assignment_strategy",
                            "every_member_assignment_strategy",
                          ],
                          type: "string",
                        },
                        members: {
                          description:
                            "The members participating in this assignment strategy",
                          items: {
                            additionalProperties: false,
                            properties: {
                              type: {
                                description:
                                  "'empty_member' is a deliberately unfilled slot",
                                enum: ["user_member", "empty_member"],
                                type: "string",
                              },
                              user_id: {
                                anyOf: [
                                  {
                                    type: "string",
                                  },
                                  {
                                    type: "null",
                                  },
                                ],
                                default: null,
                                description: "Required for user_member",
                              },
                            },
                            required: ["type"],
                            type: "object",
                          },
                          maxItems: 20,
                          type: "array",
                        },
                        shifts_per_member: {
                          anyOf: [
                            {
                              type: "integer",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description:
                            "Number of consecutive shifts each member covers before rotating to the next. Required when type is 'rotating_member_assignment_strategy'",
                        },
                      },
                      required: ["type", "members"],
                      type: "object",
                      description: "The assignment strategy for this event",
                    },
                  },
                  required: [
                    "name",
                    "start_time",
                    "end_time",
                    "effective_since",
                    "recurrence",
                    "assignment_strategy",
                  ],
                  type: "object",
                  description:
                    "The event's name, timing, recurrence, and assignment strategy",
                },
              },
              required: ["schedule_id", "rotation_id", "event_data"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "update_rotation_event",
                  default: "update_rotation_event",
                  type: "string",
                },
                schedule_id: {
                  description: "The ID of the schedule the rotation belongs to",
                  type: "string",
                },
                rotation_id: {
                  description: "The ID of the rotation the event belongs to",
                  type: "string",
                },
                event_id: {
                  description: "The ID of the rotation event to update",
                  type: "string",
                },
                event_data: {
                  additionalProperties: false,
                  properties: {
                    name: {
                      description: "The name of the rotation event",
                      type: "string",
                    },
                    start_time: {
                      properties: {
                        date_time: {
                          description:
                            "Date-time. ISO 8601 with UTC offset, e.g. '2026-09-04T13:00:00Z'",
                          format: "date-time",
                          type: "string",
                        },
                        time_zone: {
                          description:
                            "IANA time zone name, e.g. 'America/New_York'",
                          type: "string",
                        },
                      },
                      required: ["date_time", "time_zone"],
                      type: "object",
                      description: "The start time of the event",
                    },
                    end_time: {
                      properties: {
                        date_time: {
                          description:
                            "Date-time. ISO 8601 with UTC offset, e.g. '2026-09-04T13:00:00Z'",
                          format: "date-time",
                          type: "string",
                        },
                        time_zone: {
                          description:
                            "IANA time zone name, e.g. 'America/New_York'",
                          type: "string",
                        },
                      },
                      required: ["date_time", "time_zone"],
                      type: "object",
                      description: "The end time of the event",
                    },
                    effective_since: {
                      description:
                        "When the event takes effect; past values are clamped to now. ISO 8601 with UTC offset, e.g. '2026-09-04T13:00:00Z'",
                      format: "date-time",
                      type: "string",
                    },
                    effective_until: {
                      anyOf: [
                        {
                          format: "date-time",
                          type: "string",
                        },
                        {
                          type: "null",
                        },
                      ],
                      default: null,
                      description:
                        "When the event stops being effective; omit for indefinite. ISO 8601 with UTC offset, e.g. '2026-09-04T13:00:00Z'",
                    },
                    recurrence: {
                      description:
                        "RFC 5545 rules: exactly one RRULE (e.g. 'RRULE:FREQ=WEEKLY;BYDAY=MO,TU'), plus optional EXDATE/RDATE entries",
                      items: {
                        type: "string",
                      },
                      type: "array",
                    },
                    assignment_strategy: {
                      additionalProperties: false,
                      properties: {
                        type: {
                          description:
                            "'rotating_member_assignment_strategy' hands off to the next member after shifts_per_member shifts; 'every_member_assignment_strategy' puts all members on every shift",
                          enum: [
                            "rotating_member_assignment_strategy",
                            "every_member_assignment_strategy",
                          ],
                          type: "string",
                        },
                        members: {
                          description:
                            "The members participating in this assignment strategy",
                          items: {
                            additionalProperties: false,
                            properties: {
                              type: {
                                description:
                                  "'empty_member' is a deliberately unfilled slot",
                                enum: ["user_member", "empty_member"],
                                type: "string",
                              },
                              user_id: {
                                anyOf: [
                                  {
                                    type: "string",
                                  },
                                  {
                                    type: "null",
                                  },
                                ],
                                default: null,
                                description: "Required for user_member",
                              },
                            },
                            required: ["type"],
                            type: "object",
                          },
                          maxItems: 20,
                          type: "array",
                        },
                        shifts_per_member: {
                          anyOf: [
                            {
                              type: "integer",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description:
                            "Number of consecutive shifts each member covers before rotating to the next. Required when type is 'rotating_member_assignment_strategy'",
                        },
                      },
                      required: ["type", "members"],
                      type: "object",
                      description: "The assignment strategy for this event",
                    },
                  },
                  required: [
                    "name",
                    "start_time",
                    "end_time",
                    "effective_since",
                    "recurrence",
                    "assignment_strategy",
                  ],
                  type: "object",
                  description:
                    "Full replacement; partial bodies are rejected. For a started event only effective_until may change: resend the other fields exactly as get_rotation_event returns them. To change a started event's members, end it with effective_until and create a successor event.",
                },
              },
              required: [
                "schedule_id",
                "rotation_id",
                "event_id",
                "event_data",
              ],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "delete_rotation_event",
                  default: "delete_rotation_event",
                  type: "string",
                },
                schedule_id: {
                  description: "The ID of the schedule the rotation belongs to",
                  type: "string",
                },
                rotation_id: {
                  description: "The ID of the rotation the event belongs to",
                  type: "string",
                },
                event_id: {
                  description: "The ID of the rotation event to delete",
                  type: "string",
                },
              },
              required: ["schedule_id", "rotation_id", "event_id"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "create_custom_shifts",
                  default: "create_custom_shifts",
                  type: "string",
                },
                schedule_id: {
                  description:
                    "The ID of the schedule to create the custom shifts on",
                  type: "string",
                },
                shifts: {
                  description: "The custom shifts to create",
                  items: {
                    additionalProperties: false,
                    properties: {
                      start_time: {
                        description:
                          "Start time of the shift. ISO 8601 with UTC offset, e.g. '2026-09-04T13:00:00Z'",
                        format: "date-time",
                        type: "string",
                      },
                      end_time: {
                        description:
                          "End time of the shift. ISO 8601 with UTC offset, e.g. '2026-09-04T13:00:00Z'",
                        format: "date-time",
                        type: "string",
                      },
                      assignments: {
                        description:
                          "The single assignment covering this shift (exactly one entry)",
                        items: {
                          additionalProperties: false,
                          properties: {
                            member: {
                              additionalProperties: false,
                              properties: {
                                type: {
                                  description:
                                    "'empty_member' is a deliberately unfilled slot",
                                  enum: ["user_member", "empty_member"],
                                  type: "string",
                                },
                                user_id: {
                                  anyOf: [
                                    {
                                      type: "string",
                                    },
                                    {
                                      type: "null",
                                    },
                                  ],
                                  default: null,
                                  description: "Required for user_member",
                                },
                              },
                              required: ["type"],
                              type: "object",
                              description:
                                "The member assigned to this custom shift",
                            },
                          },
                          required: ["member"],
                          type: "object",
                        },
                        type: "array",
                      },
                    },
                    required: ["start_time", "end_time", "assignments"],
                    type: "object",
                  },
                  type: "array",
                },
              },
              required: ["schedule_id", "shifts"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "update_custom_shift",
                  default: "update_custom_shift",
                  type: "string",
                },
                schedule_id: {
                  description:
                    "The ID of the schedule the custom shift belongs to",
                  type: "string",
                },
                custom_shift_id: {
                  description: "The ID of the custom shift to update",
                  type: "string",
                },
                shift_data: {
                  additionalProperties: false,
                  properties: {
                    start_time: {
                      description:
                        "Start time of the shift. ISO 8601 with UTC offset, e.g. '2026-09-04T13:00:00Z'",
                      format: "date-time",
                      type: "string",
                    },
                    end_time: {
                      description:
                        "End time of the shift. ISO 8601 with UTC offset, e.g. '2026-09-04T13:00:00Z'",
                      format: "date-time",
                      type: "string",
                    },
                    assignments: {
                      description:
                        "The single assignment covering this shift (exactly one entry)",
                      items: {
                        additionalProperties: false,
                        properties: {
                          member: {
                            additionalProperties: false,
                            properties: {
                              type: {
                                description:
                                  "'empty_member' is a deliberately unfilled slot",
                                enum: ["user_member", "empty_member"],
                                type: "string",
                              },
                              user_id: {
                                anyOf: [
                                  {
                                    type: "string",
                                  },
                                  {
                                    type: "null",
                                  },
                                ],
                                default: null,
                                description: "Required for user_member",
                              },
                            },
                            required: ["type"],
                            type: "object",
                            description:
                              "The member assigned to this custom shift",
                          },
                        },
                        required: ["member"],
                        type: "object",
                      },
                      type: "array",
                    },
                  },
                  required: ["start_time", "end_time", "assignments"],
                  type: "object",
                  description: "The custom shift's replacement data",
                },
              },
              required: ["schedule_id", "custom_shift_id", "shift_data"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "delete_custom_shift",
                  default: "delete_custom_shift",
                  type: "string",
                },
                schedule_id: {
                  description:
                    "The ID of the schedule the custom shift belongs to",
                  type: "string",
                },
                custom_shift_id: {
                  description: "The ID of the custom shift to delete",
                  type: "string",
                },
              },
              required: ["schedule_id", "custom_shift_id"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "create_overrides",
                  default: "create_overrides",
                  type: "string",
                },
                schedule_id: {
                  description: "The ID of the schedule to create overrides for",
                  type: "string",
                },
                overrides: {
                  description: "The overrides to create",
                  items: {
                    additionalProperties: false,
                    properties: {
                      rotation_id: {
                        anyOf: [
                          {
                            type: "string",
                          },
                          {
                            type: "null",
                          },
                        ],
                        default: null,
                        description:
                          "The rotation whose shift is being overridden. Mutually exclusive with custom_shift_id \u2014 exactly one must be set",
                      },
                      custom_shift_id: {
                        anyOf: [
                          {
                            type: "string",
                          },
                          {
                            type: "null",
                          },
                        ],
                        default: null,
                        description:
                          "The custom shift being overridden. Mutually exclusive with rotation_id \u2014 exactly one must be set",
                      },
                      start_time: {
                        description:
                          "Start time of the override. ISO 8601 with UTC offset, e.g. '2026-09-04T13:00:00Z'",
                        format: "date-time",
                        type: "string",
                      },
                      end_time: {
                        description:
                          "End time of the override. ISO 8601 with UTC offset, e.g. '2026-09-04T13:00:00Z'",
                        format: "date-time",
                        type: "string",
                      },
                      overridden_member: {
                        additionalProperties: false,
                        properties: {
                          type: {
                            description:
                              "'empty_member' is a deliberately unfilled slot",
                            enum: ["user_member", "empty_member"],
                            type: "string",
                          },
                          user_id: {
                            anyOf: [
                              {
                                type: "string",
                              },
                              {
                                type: "null",
                              },
                            ],
                            default: null,
                            description: "Required for user_member",
                          },
                        },
                        required: ["type"],
                        type: "object",
                        description: "The member being overridden",
                      },
                      overriding_member: {
                        additionalProperties: false,
                        properties: {
                          type: {
                            description:
                              "'empty_member' is a deliberately unfilled slot",
                            enum: ["user_member", "empty_member"],
                            type: "string",
                          },
                          user_id: {
                            anyOf: [
                              {
                                type: "string",
                              },
                              {
                                type: "null",
                              },
                            ],
                            default: null,
                            description: "Required for user_member",
                          },
                        },
                        required: ["type"],
                        type: "object",
                        description: "The member covering the override",
                      },
                    },
                    required: [
                      "start_time",
                      "end_time",
                      "overridden_member",
                      "overriding_member",
                    ],
                    type: "object",
                  },
                  type: "array",
                },
              },
              required: ["schedule_id", "overrides"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "update_override",
                  default: "update_override",
                  type: "string",
                },
                schedule_id: {
                  description: "The ID of the schedule the override belongs to",
                  type: "string",
                },
                override_id: {
                  description: "The ID of the override to update",
                  type: "string",
                },
                override_data: {
                  additionalProperties: false,
                  properties: {
                    start_time: {
                      description:
                        "Start time of the override. ISO 8601 with UTC offset, e.g. '2026-09-04T13:00:00Z'",
                      format: "date-time",
                      type: "string",
                    },
                    end_time: {
                      description:
                        "End time of the override. ISO 8601 with UTC offset, e.g. '2026-09-04T13:00:00Z'",
                      format: "date-time",
                      type: "string",
                    },
                    overridden_member: {
                      additionalProperties: false,
                      properties: {
                        type: {
                          description:
                            "'empty_member' is a deliberately unfilled slot",
                          enum: ["user_member", "empty_member"],
                          type: "string",
                        },
                        user_id: {
                          anyOf: [
                            {
                              type: "string",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description: "Required for user_member",
                        },
                      },
                      required: ["type"],
                      type: "object",
                      description: "The member being overridden",
                    },
                    overriding_member: {
                      additionalProperties: false,
                      properties: {
                        type: {
                          description:
                            "'empty_member' is a deliberately unfilled slot",
                          enum: ["user_member", "empty_member"],
                          type: "string",
                        },
                        user_id: {
                          anyOf: [
                            {
                              type: "string",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description: "Required for user_member",
                        },
                      },
                      required: ["type"],
                      type: "object",
                      description: "The member covering the override",
                    },
                  },
                  required: [
                    "start_time",
                    "end_time",
                    "overridden_member",
                    "overriding_member",
                  ],
                  type: "object",
                  description: "The replacement override data",
                },
              },
              required: ["schedule_id", "override_id", "override_data"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "delete_override",
                  default: "delete_override",
                  type: "string",
                },
                schedule_id: {
                  description: "The ID of the schedule the override belongs to",
                  type: "string",
                },
                override_id: {
                  description: "The ID of the override to delete",
                  type: "string",
                },
              },
              required: ["schedule_id", "override_id"],
              type: "object",
            },
          ],
        },
      },
      required: ["request"],
      type: "object",
    },
    annotations: {
      readOnlyHint: false,
      destructiveHint: true,
      idempotentHint: false,
    },
  },
  {
    name: "manage_services",
    description:
      "Create or update PagerDuty services.\n\nActions: create (new service), update (modify an existing service).\n\nActions:\n- create: required=[service_data.service], optional=[]\n- update: required=[service_id, service_data.service], optional=[]",
    inputSchema: {
      additionalProperties: false,
      properties: {
        request: {
          oneOf: [
            {
              properties: {
                action: {
                  const: "create",
                  default: "create",
                  type: "string",
                },
                service_data: {
                  properties: {
                    service: {
                      properties: {
                        id: {
                          anyOf: [
                            {
                              type: "string",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description: "The ID of the service",
                        },
                        name: {
                          anyOf: [
                            {
                              type: "string",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description: "The name of the service",
                        },
                        description: {
                          anyOf: [
                            {
                              type: "string",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description: "The description of the service",
                        },
                        escalation_policy: {
                          description: "Reference to an escalation policy.",
                          properties: {
                            id: {
                              description: "The ID of the escalation policy",
                              type: "string",
                            },
                            summary: {
                              anyOf: [
                                {
                                  type: "string",
                                },
                                {
                                  type: "null",
                                },
                              ],
                              default: null,
                              description:
                                "Read-only. A short-form, server-generated string that provides succinct information about the escalation policy.",
                            },
                          },
                          required: ["id"],
                          type: "object",
                        },
                        teams: {
                          anyOf: [
                            {
                              items: {
                                description: "Reference to a team.",
                                properties: {
                                  id: {
                                    description:
                                      "The ID of the referenced object",
                                    type: "string",
                                  },
                                  summary: {
                                    anyOf: [
                                      {
                                        type: "string",
                                      },
                                      {
                                        type: "null",
                                      },
                                    ],
                                    default: null,
                                    description:
                                      "A short-form, server-generated string that provides succinct information about the referenced object",
                                  },
                                },
                                required: ["id"],
                                type: "object",
                              },
                              type: "array",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description:
                            "List of teams associated with the service",
                        },
                        status: {
                          anyOf: [
                            {
                              type: "string",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description: "The current state of the service",
                        },
                      },
                      required: ["escalation_policy"],
                      type: "object",
                      description: "The service to create",
                    },
                  },
                  required: ["service"],
                  type: "object",
                },
              },
              required: ["service_data"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "update",
                  default: "update",
                  type: "string",
                },
                service_id: {
                  type: "string",
                },
                service_data: {
                  properties: {
                    service: {
                      properties: {
                        id: {
                          anyOf: [
                            {
                              type: "string",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description: "The ID of the service",
                        },
                        name: {
                          anyOf: [
                            {
                              type: "string",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description: "The name of the service",
                        },
                        description: {
                          anyOf: [
                            {
                              type: "string",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description: "The description of the service",
                        },
                        escalation_policy: {
                          description: "Reference to an escalation policy.",
                          properties: {
                            id: {
                              description: "The ID of the escalation policy",
                              type: "string",
                            },
                            summary: {
                              anyOf: [
                                {
                                  type: "string",
                                },
                                {
                                  type: "null",
                                },
                              ],
                              default: null,
                              description:
                                "Read-only. A short-form, server-generated string that provides succinct information about the escalation policy.",
                            },
                          },
                          required: ["id"],
                          type: "object",
                        },
                        teams: {
                          anyOf: [
                            {
                              items: {
                                description: "Reference to a team.",
                                properties: {
                                  id: {
                                    description:
                                      "The ID of the referenced object",
                                    type: "string",
                                  },
                                  summary: {
                                    anyOf: [
                                      {
                                        type: "string",
                                      },
                                      {
                                        type: "null",
                                      },
                                    ],
                                    default: null,
                                    description:
                                      "A short-form, server-generated string that provides succinct information about the referenced object",
                                  },
                                },
                                required: ["id"],
                                type: "object",
                              },
                              type: "array",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description:
                            "List of teams associated with the service",
                        },
                        status: {
                          anyOf: [
                            {
                              type: "string",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description: "The current state of the service",
                        },
                      },
                      required: ["escalation_policy"],
                      type: "object",
                      description: "The service to create",
                    },
                  },
                  required: ["service"],
                  type: "object",
                },
              },
              required: ["service_id", "service_data"],
              type: "object",
            },
          ],
        },
      },
      required: ["request"],
      type: "object",
    },
    annotations: {
      readOnlyHint: false,
      destructiveHint: true,
      idempotentHint: false,
    },
  },
  {
    name: "manage_status_pages",
    description:
      "Create posts and post updates on PagerDuty status pages.\n\nActions: create_post (new status page post with at least one update),\ncreate_post_update (add a new update to an existing post).\n\nActions:\n- create_post: required=[status_page_id, create_model.post], optional=[]\n- create_post_update: required=[status_page_id, post_id, create_model.post_update], optional=[]",
    inputSchema: {
      additionalProperties: false,
      properties: {
        request: {
          oneOf: [
            {
              properties: {
                action: {
                  const: "create_post",
                  default: "create_post",
                  type: "string",
                },
                status_page_id: {
                  type: "string",
                },
                create_model: {
                  properties: {
                    post: {
                      properties: {
                        title: {
                          description: "The title given to a Post",
                          type: "string",
                        },
                        post_type: {
                          description: "The type of the Post",
                          enum: ["incident", "maintenance"],
                          type: "string",
                        },
                        starts_at: {
                          description:
                            "The date and time the Post intent becomes effective (required for both incident and maintenance posts)",
                          format: "date-time",
                          type: "string",
                        },
                        ends_at: {
                          description:
                            "The date and time the Post intent is concluded (required for both incident and maintenance posts)",
                          format: "date-time",
                          type: "string",
                        },
                        updates: {
                          description:
                            "Post Updates to be associated with a Post. At least one update is required when creating a post.",
                          items: {
                            properties: {
                              message: {
                                description: "The message of the Post Update",
                                type: "string",
                              },
                              status: {
                                properties: {
                                  id: {
                                    description:
                                      "Status page Status unique identifier",
                                    type: "string",
                                  },
                                  type: {
                                    anyOf: [
                                      {
                                        type: "string",
                                      },
                                      {
                                        type: "null",
                                      },
                                    ],
                                    default: "status_page_status",
                                    description:
                                      "A string that determines the schema of the object",
                                  },
                                },
                                required: ["id"],
                                type: "object",
                                description:
                                  "Status Page Status reference (required when creating posts)",
                              },
                              severity: {
                                properties: {
                                  id: {
                                    description:
                                      "Status page Severity unique identifier",
                                    type: "string",
                                  },
                                  type: {
                                    anyOf: [
                                      {
                                        type: "string",
                                      },
                                      {
                                        type: "null",
                                      },
                                    ],
                                    default: "status_page_severity",
                                    description:
                                      "A string that determines the schema of the object",
                                  },
                                },
                                required: ["id"],
                                type: "object",
                                description:
                                  "Status Page Severity reference (required when creating posts)",
                              },
                              impacted_services: {
                                description:
                                  "Impacted services represent the status page services affected by a post update. Can be empty list if no services are impacted.",
                                items: {
                                  properties: {
                                    service: {
                                      properties: {
                                        id: {
                                          description:
                                            "An unique identifier within Status Page scope that defines a Service entry",
                                          type: "string",
                                        },
                                        type: {
                                          anyOf: [
                                            {
                                              type: "string",
                                            },
                                            {
                                              type: "null",
                                            },
                                          ],
                                          default: "status_page_service",
                                          description:
                                            "The type of the object returned by the API",
                                        },
                                      },
                                      required: ["id"],
                                      type: "object",
                                      description:
                                        "Status Page Service reference",
                                    },
                                    impact: {
                                      properties: {
                                        id: {
                                          description:
                                            "Status page Impact unique identifier",
                                          type: "string",
                                        },
                                        type: {
                                          anyOf: [
                                            {
                                              type: "string",
                                            },
                                            {
                                              type: "null",
                                            },
                                          ],
                                          default: "status_page_impact",
                                          description:
                                            "A string that determines the schema of the object",
                                        },
                                      },
                                      required: ["id"],
                                      type: "object",
                                      description:
                                        "Status Page Impact reference",
                                    },
                                  },
                                  required: ["service", "impact"],
                                  type: "object",
                                },
                                type: "array",
                              },
                              update_frequency_ms: {
                                anyOf: [
                                  {
                                    type: "integer",
                                  },
                                  {
                                    type: "null",
                                  },
                                ],
                                default: null,
                                description:
                                  "The frequency of the next Post Update in milliseconds. Use null for no frequency.",
                              },
                              notify_subscribers: {
                                default: false,
                                description:
                                  "Determines if the subscribers should be notified of the Post Update",
                                type: "boolean",
                              },
                              reported_at: {
                                anyOf: [
                                  {
                                    format: "date-time",
                                    type: "string",
                                  },
                                  {
                                    type: "null",
                                  },
                                ],
                                default: null,
                                description:
                                  "The date and time the Post Update was reported",
                              },
                              post: {
                                anyOf: [
                                  {
                                    properties: {
                                      id: {
                                        description:
                                          "Status page post unique identifier",
                                        type: "string",
                                      },
                                      type: {
                                        anyOf: [
                                          {
                                            type: "string",
                                          },
                                          {
                                            type: "null",
                                          },
                                        ],
                                        default: "status_page_post",
                                        description:
                                          "A string that determines the schema of the object",
                                      },
                                    },
                                    required: ["id"],
                                    type: "object",
                                  },
                                  {
                                    type: "null",
                                  },
                                ],
                                default: null,
                                description: "Status Page Post reference",
                              },
                            },
                            required: ["message", "status", "severity"],
                            type: "object",
                          },
                          type: "array",
                        },
                        status_page: {
                          properties: {
                            id: {
                              description: "Status page unique identifier",
                              type: "string",
                            },
                            type: {
                              anyOf: [
                                {
                                  type: "string",
                                },
                                {
                                  type: "null",
                                },
                              ],
                              default: "status_page",
                              description:
                                "A string that determines the schema of the object",
                            },
                          },
                          required: ["id"],
                          type: "object",
                          description: "Status Page reference",
                        },
                      },
                      required: [
                        "title",
                        "post_type",
                        "starts_at",
                        "ends_at",
                        "updates",
                        "status_page",
                      ],
                      type: "object",
                      description: "The post to create",
                    },
                  },
                  required: ["post"],
                  type: "object",
                },
              },
              required: ["status_page_id", "create_model"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "create_post_update",
                  default: "create_post_update",
                  type: "string",
                },
                status_page_id: {
                  type: "string",
                },
                post_id: {
                  type: "string",
                },
                create_model: {
                  properties: {
                    post_update: {
                      properties: {
                        message: {
                          description: "The message of the Post Update",
                          type: "string",
                        },
                        status: {
                          properties: {
                            id: {
                              description:
                                "Status page Status unique identifier",
                              type: "string",
                            },
                            type: {
                              anyOf: [
                                {
                                  type: "string",
                                },
                                {
                                  type: "null",
                                },
                              ],
                              default: "status_page_status",
                              description:
                                "A string that determines the schema of the object",
                            },
                          },
                          required: ["id"],
                          type: "object",
                          description:
                            "Status Page Status reference (required when creating posts)",
                        },
                        severity: {
                          properties: {
                            id: {
                              description:
                                "Status page Severity unique identifier",
                              type: "string",
                            },
                            type: {
                              anyOf: [
                                {
                                  type: "string",
                                },
                                {
                                  type: "null",
                                },
                              ],
                              default: "status_page_severity",
                              description:
                                "A string that determines the schema of the object",
                            },
                          },
                          required: ["id"],
                          type: "object",
                          description:
                            "Status Page Severity reference (required when creating posts)",
                        },
                        impacted_services: {
                          description:
                            "Impacted services represent the status page services affected by a post update. Can be empty list if no services are impacted.",
                          items: {
                            properties: {
                              service: {
                                properties: {
                                  id: {
                                    description:
                                      "An unique identifier within Status Page scope that defines a Service entry",
                                    type: "string",
                                  },
                                  type: {
                                    anyOf: [
                                      {
                                        type: "string",
                                      },
                                      {
                                        type: "null",
                                      },
                                    ],
                                    default: "status_page_service",
                                    description:
                                      "The type of the object returned by the API",
                                  },
                                },
                                required: ["id"],
                                type: "object",
                                description: "Status Page Service reference",
                              },
                              impact: {
                                properties: {
                                  id: {
                                    description:
                                      "Status page Impact unique identifier",
                                    type: "string",
                                  },
                                  type: {
                                    anyOf: [
                                      {
                                        type: "string",
                                      },
                                      {
                                        type: "null",
                                      },
                                    ],
                                    default: "status_page_impact",
                                    description:
                                      "A string that determines the schema of the object",
                                  },
                                },
                                required: ["id"],
                                type: "object",
                                description: "Status Page Impact reference",
                              },
                            },
                            required: ["service", "impact"],
                            type: "object",
                          },
                          type: "array",
                        },
                        update_frequency_ms: {
                          anyOf: [
                            {
                              type: "integer",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description:
                            "The frequency of the next Post Update in milliseconds. Use null for no frequency.",
                        },
                        notify_subscribers: {
                          default: false,
                          description:
                            "Determines if the subscribers should be notified of the Post Update",
                          type: "boolean",
                        },
                        reported_at: {
                          anyOf: [
                            {
                              format: "date-time",
                              type: "string",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description:
                            "The date and time the Post Update was reported",
                        },
                        post: {
                          anyOf: [
                            {
                              properties: {
                                id: {
                                  description:
                                    "Status page post unique identifier",
                                  type: "string",
                                },
                                type: {
                                  anyOf: [
                                    {
                                      type: "string",
                                    },
                                    {
                                      type: "null",
                                    },
                                  ],
                                  default: "status_page_post",
                                  description:
                                    "A string that determines the schema of the object",
                                },
                              },
                              required: ["id"],
                              type: "object",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description: "Status Page Post reference",
                        },
                      },
                      required: ["message", "status", "severity"],
                      type: "object",
                      description: "The post update to create",
                    },
                  },
                  required: ["post_update"],
                  type: "object",
                },
              },
              required: ["status_page_id", "post_id", "create_model"],
              type: "object",
            },
          ],
        },
      },
      required: ["request"],
      type: "object",
    },
    annotations: {
      readOnlyHint: false,
      destructiveHint: true,
      idempotentHint: false,
    },
  },
  {
    name: "manage_teams",
    description:
      "Create, update, or delete PagerDuty teams and membership.\n\nActions: create (new team), update (modify team details), delete (remove team),\nadd_member (add a user to a team), remove_member (remove a user from a team).\n\nActions:\n- create: required=[create_model.team], optional=[]\n- update: required=[team_id, update_model.team], optional=[]\n- delete: required=[team_id], optional=[]\n- add_member: required=[team_id, member_data.user_id], optional=[member_data.role]\n- remove_member: required=[team_id, user_id], optional=[]",
    inputSchema: {
      additionalProperties: false,
      properties: {
        request: {
          oneOf: [
            {
              properties: {
                action: {
                  const: "create",
                  default: "create",
                  type: "string",
                },
                create_model: {
                  properties: {
                    team: {
                      properties: {
                        name: {
                          type: "string",
                        },
                        description: {
                          anyOf: [
                            {
                              type: "string",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description:
                            "A short-form, server-generated string that provides succinct information about the team",
                        },
                        default_role: {
                          anyOf: [
                            {
                              enum: ["manager", "none"],
                              type: "string",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: "manager",
                          description:
                            "The default role granted to new users added to the team: 'manager' gives full control over the team's membership and configuration, 'none' grants no extra team-level permissions beyond the user's account role",
                        },
                      },
                      required: ["name"],
                      type: "object",
                      description: "The team to create",
                    },
                  },
                  required: ["team"],
                  type: "object",
                },
              },
              required: ["create_model"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "update",
                  default: "update",
                  type: "string",
                },
                team_id: {
                  type: "string",
                },
                update_model: {
                  properties: {
                    team: {
                      properties: {
                        name: {
                          type: "string",
                        },
                        description: {
                          anyOf: [
                            {
                              type: "string",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: null,
                          description:
                            "A short-form, server-generated string that provides succinct information about the team",
                        },
                        default_role: {
                          anyOf: [
                            {
                              enum: ["manager", "none"],
                              type: "string",
                            },
                            {
                              type: "null",
                            },
                          ],
                          default: "manager",
                          description:
                            "The default role granted to new users added to the team: 'manager' gives full control over the team's membership and configuration, 'none' grants no extra team-level permissions beyond the user's account role",
                        },
                      },
                      required: ["name"],
                      type: "object",
                      description: "The team to create",
                    },
                  },
                  required: ["team"],
                  type: "object",
                },
              },
              required: ["team_id", "update_model"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "delete",
                  default: "delete",
                  type: "string",
                },
                team_id: {
                  type: "string",
                },
              },
              required: ["team_id"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "add_member",
                  default: "add_member",
                  type: "string",
                },
                team_id: {
                  type: "string",
                },
                member_data: {
                  properties: {
                    user_id: {
                      description: "The ID of the user to add to the team",
                      type: "string",
                    },
                    role: {
                      default: "manager",
                      description:
                        "The user's role on the team: 'observer' can view the team's objects but can't be put on-call or take incident action, 'responder' can be assigned to the team's services/schedules/escalation policies and take incident action, 'manager' additionally controls the team's membership and configuration",
                      enum: ["observer", "responder", "manager"],
                      type: "string",
                    },
                  },
                  required: ["user_id"],
                  type: "object",
                },
              },
              required: ["team_id", "member_data"],
              type: "object",
            },
            {
              properties: {
                action: {
                  const: "remove_member",
                  default: "remove_member",
                  type: "string",
                },
                team_id: {
                  type: "string",
                },
                user_id: {
                  type: "string",
                },
              },
              required: ["team_id", "user_id"],
              type: "object",
            },
          ],
        },
      },
      required: ["request"],
      type: "object",
    },
    annotations: {
      readOnlyHint: false,
      destructiveHint: true,
      idempotentHint: false,
    },
  },
];
